import * as THREE from 'three';
import {
  BLACK, BONE, BRASS, BRONZE, DSTEEL, DWOOD, GOLD, LEATHER_BELT, SKIN, STEEL, STRING, WOOD, cloth, emis, leather, metal, spectral, std, stone,
} from '../materials.ts';
import { G, M, Ribbon, V, flame, glowSprite, heaterShield, limb, ring, sword, wisp } from '../prims.ts';
import { aura, backArc, pauldron, runeRow, tierCape, type HeroTier } from './hero-tiers.ts';
import { begin, done, pointLight, type BaseOpts, type Ctx, type HeroRig, type Model, type Pose } from './types.ts';

/* ---------- humanoid figure with pivots ---------- */

export interface FigureOpts {
  legs: THREE.Material;
  boots: THREE.Material;
  torso: THREE.Material;
  shoulders?: THREE.Material;
  arms: THREE.Material;
  forearms?: THREE.Material;
  hands?: THREE.Material;
  skin?: THREE.Material;
  cape?: string;
  /** Cape size (hero tiers wear a longer, fuller one): length, bottom radius, arc in radians. */
  capeLen?: number;
  capeR?: number;
  capeArc?: number;
}

/**
 * A rigged figure. Every `*Abs` group is a frame aligned with figure space at rest, nested inside the
 * corresponding pivot, so accessories can be placed with absolute figure coordinates and still follow the limb.
 */
export interface Figure {
  fig: THREE.Group;
  rig: HeroRig;
  bodyAbs: THREE.Group;
  headAbs: THREE.Group;
  armLAbs: THREE.Group;
  armRAbs: THREE.Group;
  /** Hand pivots (local frames, as in the reference). */
  rh: THREE.Group;
  lh: THREE.Group;
  rhAbs: THREE.Group;
  lhAbs: THREE.Group;
}

const pivot = (parentAbs: THREE.Object3D, at: THREE.Vector3, name: string): { p: THREE.Group; abs: THREE.Group } => {
  const p = G(parentAbs, at.x, at.y, at.z);
  p.name = name;
  const abs = G(p, -at.x, -at.y, -at.z);
  return { p, abs };
};

const HAND_R = V(0.28, 0.91, 0.13);
const HAND_L = V(-0.28, 0.91, 0.13);

/** Joint heights of the figure (figure space): knees and elbows bend there. */
export const KNEE_Y = 0.5;
export const ELBOW_Y = 1.13;

export const figure = (o: FigureOpts): Figure => {
  const fig = new THREE.Group();
  fig.name = 'figure';
  const legs: THREE.Group[] = [];
  const shins: THREE.Group[] = [];
  for (const k of [-1, 1]) {
    const { p, abs } = pivot(fig, V(k * 0.1, 0.9, 0), k < 0 ? 'legL' : 'legR');
    limb(abs, V(k * 0.095, KNEE_Y, 0), V(k * 0.1, 0.9, 0), 0.068, 0.08, o.legs);
    M(new THREE.SphereGeometry(0.066, 8, 6), o.legs, abs).position.set(k * 0.095, KNEE_Y, 0);
    const shin = pivot(abs, V(k * 0.095, KNEE_Y, 0), k < 0 ? 'shinL' : 'shinR');
    limb(shin.abs, V(k * 0.09, 0.1, 0), V(k * 0.095, KNEE_Y, 0), 0.06, 0.068, o.legs);
    const boot = M(new THREE.BoxGeometry(0.12, 0.16, 0.22), o.boots, shin.abs);
    boot.position.set(k * 0.09, 0.08, 0.03);
    boot.name = 'boot';
    legs.push(p);
    shins.push(shin.p);
  }
  const body = pivot(fig, V(0, 0.92, 0), 'body');
  const b = body.abs;
  M(new THREE.CylinderGeometry(0.17, 0.16, 0.16, 10), o.legs, b).position.y = 0.92;
  ring(b, 0.17, 0.025, LEATHER_BELT, 0.98, 5, 14);
  const torso = M(new THREE.CylinderGeometry(0.21, 0.16, 0.5, 8), o.torso, b);
  torso.position.y = 1.22;
  torso.name = 'torso';
  const arms: { p: THREE.Group; abs: THREE.Group; fore: THREE.Group; foreAbs: THREE.Group }[] = [];
  for (const k of [-1, 1]) {
    const sh = M(new THREE.SphereGeometry(0.09, 10, 7), o.shoulders ?? o.torso, b);
    sh.position.set(k * 0.22, 1.42, 0);
    sh.scale.set(1.15, 0.85, 1.1);
    sh.name = 'shoulder';
    const arm = pivot(b, V(k * 0.23, 1.38, 0), k < 0 ? 'armL' : 'armR');
    limb(arm.abs, V(k * 0.23, 1.38, 0), V(k * 0.27, ELBOW_Y, 0.04), 0.05, 0.055, o.arms);
    M(new THREE.SphereGeometry(0.048, 8, 6), o.forearms ?? o.arms, arm.abs).position.set(k * 0.27, ELBOW_Y, 0.04);
    const fore = pivot(arm.abs, V(k * 0.27, ELBOW_Y, 0.04), k < 0 ? 'foreL' : 'foreR');
    limb(fore.abs, V(k * 0.27, ELBOW_Y, 0.04), V(k * 0.28, 0.93, 0.12), 0.045, 0.05, o.forearms ?? o.arms);
    M(new THREE.SphereGeometry(0.045, 7, 5), o.hands ?? SKIN, fore.abs).position.set(k * 0.28, 0.91, 0.13);
    arms.push({ ...arm, fore: fore.p, foreAbs: fore.abs });
  }
  limb(b, V(0, 1.44, 0), V(0, 1.53, 0), 0.05, 0.05, SKIN);
  const head = pivot(b, V(0, 1.5, 0), 'head');
  const hd = M(new THREE.SphereGeometry(0.12, 12, 10), o.skin ?? SKIN, head.abs);
  hd.position.y = 1.63;
  hd.scale.set(0.95, 1.05, 1);
  let cape: THREE.Group | undefined;
  if (o.cape) {
    const len = o.capeLen ?? 1.2, arc = o.capeArc ?? 1.9;
    const cp = pivot(b, V(0, 1.46, 0), 'cape');
    cape = cp.p;
    M(new THREE.CylinderGeometry(0.24, o.capeR ?? 0.42, len, 12, 1, true, Math.PI - arc / 2, arc), std(o.cape, { roughness: 0.95, side: THREE.DoubleSide }), cp.abs)
      .position.y = 1.46 - len / 2;
  }
  const [armL, armR] = arms as [(typeof arms)[number], (typeof arms)[number]];
  const rh = G(armR.foreAbs, HAND_R.x, HAND_R.y, HAND_R.z);
  rh.name = 'weapon';
  const lh = G(armL.foreAbs, HAND_L.x, HAND_L.y, HAND_L.z);
  lh.name = 'offhand';
  const rhAbs = G(rh, -HAND_R.x, -HAND_R.y, -HAND_R.z);
  const lhAbs = G(lh, -HAND_L.x, -HAND_L.y, -HAND_L.z);
  const rig: HeroRig = {
    body: body.p, armL: armL.p, armR: armR.p, legL: legs[0]!, legR: legs[1]!, head: head.p, weapon: rh, offhand: lh, figure: fig,
    foreL: armL.fore, foreR: armR.fore, shinL: shins[0]!, shinR: shins[1]!, ...(cape ? { cape } : {}),
  };
  return { fig, rig, bodyAbs: b, headAbs: head.abs, armLAbs: armL.abs, armRAbs: armR.abs, rh, lh, rhAbs, lhAbs };
};

/**
 * Accessories a builder hung on a whole limb (upper-arm or thigh frame) that sit below the elbow or knee
 * move onto the forearm or shin, so a bracer follows the bend.
 */
export const settleLimbs = (rig: HeroRig): void => {
  rig.figure.updateMatrixWorld(true);
  const box = new THREE.Box3();
  const c = new THREE.Vector3();
  const j = new THREE.Vector3();
  const pairs: [THREE.Group, THREE.Group | undefined][] = [[rig.armL, rig.foreL], [rig.armR, rig.foreR], [rig.legL, rig.shinL], [rig.legR, rig.shinR]];
  for (const [upper, lower] of pairs) {
    if (!lower) continue;
    const abs = upper.children[0];
    const lowerAbs = lower.children[0];
    if (!abs || !lowerAbs) continue;
    lower.getWorldPosition(j);
    for (const ch of [...abs.children]) {
      if (ch === lower) continue;
      box.setFromObject(ch);
      if (box.isEmpty()) continue;
      box.getCenter(c);
      if (c.y < j.y - 0.015) lowerAbs.attach(ch);
    }
  }
};

type Rot = [number, number, number];
interface PoseSpec { body?: Rot; head?: Rot; armL?: Rot; armR?: Rot; legL?: Rot; legR?: Rot; weapon?: Rot; offhand?: Rot; fig?: Rot; figY?: number }

/* Rotations in radians. Arms/legs hang along -Y: a negative X rotation swings them forward (+Z). */
const POSES: Record<Pose, PoseSpec> = {
  idle: {},
  walkA: { legL: [-0.45, 0, 0], legR: [0.45, 0, 0], armL: [0.35, 0, 0], armR: [-0.35, 0, 0], body: [0.05, 0.06, 0] },
  walkB: { legL: [0.45, 0, 0], legR: [-0.45, 0, 0], armL: [-0.35, 0, 0], armR: [0.35, 0, 0], body: [0.05, -0.06, 0] },
  attackA: { armR: [-2.7, 0, 0.25], armL: [-0.5, 0, 0.2], body: [-0.08, -0.35, 0], head: [0, 0.2, 0], legL: [-0.25, 0, 0], legR: [0.2, 0, 0] },
  attackB: { armR: [-1.05, 0, -0.35], weapon: [0.6, 0, 0], armL: [0.2, 0, 0], body: [0.2, 0.3, 0], head: [-0.1, -0.2, 0], legL: [-0.35, 0, 0], legR: [0.3, 0, 0] },
  cast: { armL: [-1.5, 0, 0.35], armR: [-1.6, 0, -0.2], weapon: [1.45, 0, 0], head: [-0.15, 0, 0], body: [-0.06, 0, 0] },
  down: { fig: [-1.45, 0, 0], figY: 0.2, armL: [0, 0, -0.6], armR: [0, 0, 0.7], head: [0.2, 0.5, 0], legL: [0, 0, -0.12], legR: [0, 0, 0.1] },
  work: { armR: [-1.9, 0, 0], armL: [-0.9, 0, 0.3], body: [0.35, 0, 0], head: [0.25, 0, 0], legL: [-0.2, 0, 0], legR: [0.15, 0, 0] },
  block: { armL: [-1.25, 0, 0.55], offhand: [0, 0.5, 0], armR: [-0.3, 0, 0], body: [0.08, -0.25, 0], legL: [-0.3, 0, 0], legR: [0.25, 0, 0] },
  // Healer casts: an open hand held out toward the ally, the staff kept upright; and the staff raised to the sky.
  bless: { armR: [-1.25, 0, -0.1], weapon: [0.85, 0, 0], armL: [-0.2, 0, 0.3], head: [-0.08, -0.1, 0], body: [-0.05, -0.15, 0], legL: [-0.2, 0, 0], legR: [0.15, 0, 0] },
  raise: { armR: [-2.85, 0, -0.15], weapon: [2.75, 0, 0], armL: [-0.15, 0, 0.35], head: [-0.4, 0, 0], body: [-0.14, 0, 0], legL: [-0.12, 0, -0.05], legR: [0.08, 0, 0.05] },
};

const ZERO: Rot = [0, 0, 0];
const setRot = (g: THREE.Group, r: Rot | undefined): void => {
  const [x, y, z] = r ?? ZERO;
  g.rotation.set(x, y, z);
};

/** Sets the pivot rotations of a hero rig for one of the canonical 12 fps poses (resets everything else). */
export function applyPose(rig: HeroRig, pose: Pose): void {
  const s = POSES[pose];
  setRot(rig.figure, s.fig);
  rig.figure.position.y = s.figY ?? 0;
  setRot(rig.body, s.body);
  setRot(rig.head, s.head);
  setRot(rig.armL, s.armL);
  setRot(rig.armR, s.armR);
  setRot(rig.legL, s.legL);
  setRot(rig.legR, s.legR);
  setRot(rig.weapon, s.weapon);
  setRot(rig.offhand, s.offhand);
}

export const POSE_NAMES: readonly Pose[] = ['idle', 'walkA', 'walkB', 'attackA', 'attackB', 'cast', 'down', 'work', 'block', 'bless', 'raise'];

/* ---------- ghost (sleeping player's spectre) ---------- */

export interface HeroOpts extends BaseOpts {
  /** Level tier (1–4, see hero-tiers.ts): higher tiers wear more and finer gear. Default 1. */
  tier?: HeroTier;
  /** Hex color: spectral version with rune circle and wisps. */
  ghost?: string;
}

export const ghostify = (c: Ctx, fig: THREE.Object3D, color: string, tick: (n: number) => void): ((n: number) => void) => {
  const mat = spectral(color);
  fig.traverse((o) => {
    if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).material = mat;
  });
  const runeC = new THREE.Color(color).multiplyScalar(0.6);
  const rm = emis(color, `#${runeC.getHexString()}`, 0.8);
  const circle = G(c.root, 0, 0.01, 0);
  circle.name = 'ghostRing';
  ring(circle, 0.5, 0.01, rm, 0, 4, 40);
  for (let i = 0; i < 12; i++) {
    const gl = M(new THREE.BoxGeometry(0.05, 0.008, 0.02), rm, circle);
    const a = (i / 12) * Math.PI * 2;
    gl.position.set(Math.cos(a) * 0.42, 0.01, Math.sin(a) * 0.42);
    gl.rotation.y = -a;
  }
  const light = `#${new THREE.Color(color).lerp(new THREE.Color('#ffffff'), 0.45).getHexString()}`;
  const ribs: Ribbon[] = [];
  for (let i = 0; i < 4; i++) {
    const a = i * 1.6;
    ribs.push(new Ribbon(c.g, wisp(Math.cos(a) * 0.18, Math.sin(a) * 0.18, 1.4, 8), 0.012, spectral(light), c.rng, 0.04));
  }
  return (n) => {
    tick(n);
    c.g.position.y = 0.06 + Math.sin(n * 0.5) * 0.04;
    circle.rotation.y = n * 0.08;
    for (const r of ribs) r.step();
  };
};

const hero = (c: Ctx, f: Pick<Figure, 'fig' | 'rig'>, o: HeroOpts | undefined, height: number, tick: (n: number) => void = () => undefined): Model => {
  if (!f.fig.parent) c.g.add(f.fig);
  settleLimbs(f.rig);
  const t = o?.ghost ? ghostify(c, f.fig, o.ghost, tick) : tick;
  return done(c, height, t, f.rig);
};

/**
 * The class's own hat, hood or helm, gathered in one group on the head frame: a worn head piece takes its place
 * (models/dress.ts). Face, hair and beard stay out of it.
 */
const headgear = (h: THREE.Object3D): THREE.Group => {
  const g = G(h);
  g.name = 'headgear';
  return g;
};

/* ---------- heroes ---------- */

export const warrior = (o?: HeroOpts): Model => {
  const c = begin(o);
  const t = o?.tier ?? 1;
  const f = figure({
    legs: STEEL, boots: DSTEEL, torso: metal('#a7aeb6', { flatShading: true }), shoulders: STEEL, arms: DSTEEL, forearms: STEEL, hands: DSTEEL, cape: '#1f3157', ...tierCape(t),
  });
  const b = f.bodyAbs, h = headgear(f.headAbs);
  const red = cloth('#7a2328');
  const tab = M(new THREE.BoxGeometry(0.24, 0.62, 0.015), cloth('#6e1f24'), b);
  tab.position.set(0, 1.02, 0.17);
  tab.rotation.x = -0.06;
  M(new THREE.BoxGeometry(0.26, 0.03, 0.02), GOLD, b).position.set(0, 1.33, 0.175);
  M(new THREE.TorusGeometry(0.05, 0.012, 5, 12), GOLD, b).position.set(0, 1.15, 0.18);
  const helm = M(new THREE.SphereGeometry(0.135, 12, 10), STEEL, h);
  helm.position.y = 1.65;
  helm.scale.set(1, 1.08, 1.05);
  M(new THREE.BoxGeometry(0.2, 0.03, 0.05), std('#0c0a0c', { roughness: 0.5 }), h).position.set(0, 1.64, 0.12);
  if (t < 3) M(new THREE.BoxGeometry(0.03, 0.09, 0.26), red, h).position.set(0, 1.8, -0.02);
  const sw0 = t >= 3 ? 0.46 : 0.42, sh0 = t >= 3 ? 0.6 : 0.56;
  const sh = heaterShield(sw0, sh0, metal('#1f3a6e', { roughness: 0.5 }));
  f.lh.add(sh);
  sh.position.set(-0.06, 0.08, 0.02);
  sh.rotation.y = -0.35;
  M(new THREE.TorusGeometry(0.07, 0.016, 5, 14), GOLD, sh).position.set(0, 0.06, 0.06);
  for (let i = 0; i < 8; i++) {
    const ray = M(new THREE.BoxGeometry(0.012, 0.05, 0.01), GOLD, sh);
    const a = (i / 8) * Math.PI * 2;
    ray.position.set(Math.cos(a) * 0.11, 0.06 + Math.sin(a) * 0.11, 0.06);
    ray.rotation.z = a - Math.PI / 2;
  }
  const len = [0.72, 0.78, 0.86, 0.92][t - 1]!;
  const sw = sword(len, STEEL, t >= 2 ? BRONZE : GOLD);
  f.rh.add(sw);
  sw.rotation.set(0.35, 0, -0.12);

  // II: steel pauldrons, a gorget and a browband on the helm.
  if (t >= 2) {
    const trim = t >= 3 ? metal('#8e1f22', { roughness: 0.4 }) : DSTEEL;
    for (const k of [-1, 1]) pauldron(b, k, STEEL, t >= 3 ? 1.25 : 1.05, t >= 3 ? 2 : 1, trim);
    ring(b, 0.12, 0.03, STEEL, 1.47, 5, 14);
    ring(h, 0.137, 0.012, DSTEEL, 1.6, 4, 16);
    // A broad crossguard on the longer sword.
    M(new THREE.BoxGeometry(0.28, 0.035, 0.045), DSTEEL, sw).position.y = 0.11;
  }
  // III: a tall red horsehair crest, a steel-rimmed bigger shield with a boss, plate tassets and a dark collar.
  if (t >= 3) {
    M(new THREE.BoxGeometry(0.04, 0.12, 0.3), red, h).position.set(0, 1.82, -0.02);
    limb(h, V(0, 1.84, -0.15), V(0, 1.58, -0.32), 0.03, 0.012, red, 5);
    const rim = heaterShield(sw0 + 0.05, sh0 + 0.05, STEEL);
    sh.add(rim);
    rim.position.z = -0.014;
    M(new THREE.SphereGeometry(0.04, 8, 5), STEEL, sh).position.set(0, 0.06, 0.075);
    for (const k of [-1, 0, 1]) {
      const ts = M(new THREE.BoxGeometry(0.11, 0.17, 0.02), STEEL, b);
      ts.position.set(k * 0.12, 0.83, 0.16 - Math.abs(k) * 0.04);
      ts.rotation.set(-0.15, k * 0.5, 0);
    }
    const fur = M(new THREE.TorusGeometry(0.17, 0.05, 5, 14), std('#3a3530', { roughness: 1, bumpScale: 0.03 }), b);
    fur.rotation.x = Math.PI / 2;
    fur.position.set(0, 1.47, -0.03);
    fur.scale.set(1.15, 1, 1);
  }
  // IV: a winged helm, ember runes along the blade and a red-lit aura.
  if (t >= 4) {
    for (const k of [-1, 1]) {
      // Three swept steel feathers on each side of the helm.
      for (let i = 0; i < 3; i++) {
        const w = M(new THREE.BoxGeometry(0.02, 0.05, 0.26 - i * 0.05), STEEL, h);
        w.position.set(k * (0.135 + i * 0.01), 1.7 + i * 0.045, -0.1 - i * 0.01);
        w.rotation.set(0.55 + i * 0.25, 0, -k * 0.25);
      }
    }
    const rune = emis('#ffd2c8', '#ff4f3a', 1.3);
    runeRow(sw, rune, V(0, 0.26, 0.007), V(0, len - 0.02, 0.007), 5, 0.02, 0.04, 0.004);
    glowSprite(sw, '#ff6a50', 0.32, V(0, len * 0.6, 0.02), 0.5);
    glowSprite(sh, '#ff8a6e', 0.3, V(0, 0.06, 0.1), 0.45);
    aura(c.g, '#ff6a50');
  }
  return hero(c, f, o, t >= 3 ? 1.95 : 1.86);
};

export const artificer = (o?: HeroOpts): Model => {
  const c = begin(o, 0.62, 2);
  const t = o?.tier ?? 1;
  const coat = leather('#5c3f25');
  const f = figure({ legs: cloth('#3d3328'), boots: leather('#2a1d12'), torso: coat, arms: coat });
  const b = f.bodyAbs, h = headgear(f.headAbs);
  M(new THREE.CylinderGeometry(0.19, 0.3, 0.5, 10, 1, true), std('#5c3f25', { side: THREE.DoubleSide }), b).position.y = 0.72;
  M(new THREE.BoxGeometry(0.26, 0.5, 0.015), leather('#7a5634'), b).position.set(0, 0.98, 0.18);
  M(new THREE.SphereGeometry(0.125, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2.2), cloth('#4a2c18'), h).position.y = 1.65;
  ring(h, 0.125, 0.018, LEATHER_BELT, 1.7, 5, 16);
  const lens = emis('#9fe3f2', '#46c4e6', 0.3);
  for (const k of [-1, 1]) {
    const gog = M(new THREE.CylinderGeometry(0.04, 0.04, 0.04, 10), BRASS, h);
    gog.rotation.x = Math.PI / 2;
    gog.position.set(k * 0.05, 1.73, 0.11);
    M(new THREE.CircleGeometry(0.032, 10), lens, h).position.set(k * 0.05, 1.73, 0.132);
  }
  // Gear gauntlet on the left forearm.
  M(new THREE.CylinderGeometry(0.07, 0.08, 0.2, 10), BRASS, f.armLAbs).position.set(-0.28, 0.99, 0.1);
  const gear = M(new THREE.TorusGeometry(0.05, 0.015, 5, 10), BRONZE, f.armLAbs);
  gear.position.set(-0.35, 1.0, 0.1);
  gear.rotation.y = Math.PI / 2;
  // Backpack with alchemy.
  M(new THREE.BoxGeometry(0.32, 0.38, 0.16), leather('#4a3320'), b).position.set(0, 1.2, -0.24);
  for (const k of [-1, 1]) limb(b, V(k * 0.1, 1.2, -0.33), V(k * 0.1, 1.55, -0.33), 0.025, 0.025, BRONZE, 6);
  M(new THREE.CylinderGeometry(0.04, 0.04, 0.12, 8), emis('#7dffb0', '#1fd27a', 0.5, { transparent: true, opacity: 0.8 }), b).position.set(0.12, 1.1, -0.34);
  const hammer = G(f.rh);
  const hl = t >= 3 ? 0.58 : 0.5;
  limb(hammer, V(0, -0.05, 0), V(0, hl, 0), 0.02, 0.02, WOOD, 6);
  const hs = [1, 1.2, 1.4, 1.5][t - 1]!;
  M(new THREE.BoxGeometry(0.16 * hs, 0.09 * hs, 0.09 * hs), DSTEEL, hammer).position.y = hl;

  // II: a brass pauldron on the hammer arm, a tool belt of pouches and brass bands on the hammer.
  if (t >= 2) {
    pauldron(b, 1, BRASS, 1.2, 2, DSTEEL);
    ring(b, 0.172, 0.02, LEATHER_BELT, 0.9, 4, 14);
    // A riveted leather gorget.
    ring(b, 0.12, 0.035, leather('#3e2a18'), 1.47, 5, 14);
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      M(new THREE.SphereGeometry(0.012, 5, 3), BRASS, b).position.set(Math.sin(a) * 0.15, 1.47, Math.cos(a) * 0.15);
    }
    for (const a of [0.7, 1.4, -0.8, -1.5]) {
      const pouch = M(new THREE.BoxGeometry(0.08, 0.09, 0.05), leather('#3e2a18'), b);
      pouch.position.set(Math.sin(a) * 0.19, 0.86, Math.cos(a) * 0.19);
      pouch.rotation.y = a;
    }
    for (const k of [-1, 1]) {
      const band = M(new THREE.BoxGeometry(0.02, 0.095 * hs, 0.095 * hs), BRASS, hammer);
      band.position.set(k * 0.05 * hs, hl, 0);
    }
  }
  // III: a second pauldron, a great gear and twin exhausts on the pack, and a piston behind the hammer head.
  if (t >= 3) {
    pauldron(b, -1, DSTEEL, 1.05, 2, BRASS);
    const big = G(b, 0, 1.24, -0.33);
    M(new THREE.TorusGeometry(0.11, 0.025, 5, 16), BRONZE, big);
    M(new THREE.CylinderGeometry(0.035, 0.035, 0.03, 8), BRASS, big).rotation.x = Math.PI / 2;
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      const tooth = M(new THREE.BoxGeometry(0.035, 0.04, 0.03), BRONZE, big);
      tooth.position.set(Math.cos(a) * 0.14, Math.sin(a) * 0.14, 0);
      tooth.rotation.z = a;
    }
    for (const k of [-1, 1]) {
      limb(b, V(k * 0.13, 1.35, -0.3), V(k * 0.15, 1.78, -0.3), 0.028, 0.032, DSTEEL, 7);
      ring(b, 0.036, 0.01, BRASS, 1.76, 3, 10).position.set(k * 0.15, 1.76, -0.3);
    }
    M(new THREE.CylinderGeometry(0.025, 0.025, 0.12, 6), BRASS, hammer).position.set(0, hl - 0.11, 0);
  }
  // IV: a cyan arc-core burns in the chest and the hammer head, the exhausts breathe light.
  if (t >= 4) {
    const core = M(new THREE.CylinderGeometry(0.065, 0.065, 0.03, 12), BRASS, b);
    core.position.set(0, 1.3, 0.19);
    core.rotation.x = Math.PI / 2;
    M(new THREE.CircleGeometry(0.05, 12), lens, b).position.set(0, 1.3, 0.207);
    glowSprite(b, '#7fe9ff', 0.36, V(0, 1.3, 0.24), 0.7);
    for (const k of [-1, 1]) {
      M(new THREE.BoxGeometry(0.004, 0.06, 0.06), lens, hammer).position.set(k * 0.122, hl, 0);
      glowSprite(b, '#9ff0ff', 0.2, V(k * 0.15, 1.82, -0.3), 0.5);
    }
    glowSprite(hammer, '#7fe9ff', 0.3, V(0, hl, 0), 0.45);
    aura(c.g, '#5fd8f0');
  }
  return hero(c, f, o, t >= 3 ? 1.86 : 1.8, (n) => {
    gear.rotation.x = n * 0.4;
    hammer.rotation.x = n % 10 < 5 ? 0.3 : -0.2;
  });
};

export const mage = (o?: HeroOpts): Model => {
  const c = begin(o, 0.62, 3);
  const t = o?.tier ?? 1;
  const robe = cloth('#272250'), trim = metal('#b9bfd0', { roughness: 0.4 });
  const dark = std('#1b1738', { side: THREE.DoubleSide, roughness: 0.95 });
  const f = figure({ legs: robe, boots: leather('#1e1810'), torso: robe, arms: cloth('#2f2860'), cape: '#1b1738', ...tierCape(t) });
  const b = f.bodyAbs, h = f.headAbs, hg = headgear(h);
  // The skirt hangs from the hips: it lives in the figure (not the legs) so it doesn't swing with them.
  M(new THREE.ConeGeometry(0.36, 1.0, 12, 1, true), std('#272250', { side: THREE.DoubleSide, roughness: 0.95 }), f.fig).position.y = 0.5;
  ring(f.fig, 0.355, 0.015, trim, 0.02, 5, 24);
  const hood = M(new THREE.SphereGeometry(0.15, 12, 9, 0, Math.PI * 2, 0, Math.PI / 1.7), cloth('#1b1738'), hg);
  hood.position.set(0, 1.66, -0.02);
  hood.rotation.x = -0.35;
  const beard = M(new THREE.ConeGeometry(0.08, 0.3, 8), std('#c9c6bd', { roughness: 1 }), h);
  beard.position.set(0, 1.45, 0.1);
  beard.rotation.x = Math.PI + 0.15;
  const staff = G(f.rhAbs);
  const top = t >= 3 ? 2.18 : 2.0, cy = top + 0.1;
  limb(staff, V(0.33, 0, 0.14), V(0.33, top, 0.14), 0.02, 0.025, DWOOD, 6);
  const crystal = emis('#9fdcff', '#3aa6ff', 0.8, { flatShading: true, transparent: true, opacity: 0.9 });
  const cr = M(new THREE.OctahedronGeometry(0.08), crystal, staff);
  cr.position.set(0.33, cy, 0.14);
  cr.scale.set(t >= 3 ? 1.3 : 1, t >= 3 ? 2.2 : 1.7, t >= 3 ? 1.3 : 1);
  glowSprite(staff, '#5ab8ff', t >= 3 ? 0.75 : 0.55, V(0.33, cy, 0.14), 0.7);
  const runeM = emis('#bfe6ff', '#5ab8ff', 1);
  const runes: THREE.Mesh[] = [];
  const nRunes = t >= 4 ? 6 : t >= 3 ? 5 : t >= 2 ? 4 : 3;
  for (let i = 0; i < nRunes; i++) runes.push(M(new THREE.TorusGeometry(0.03, 0.006, 4, 8), runeM, staff));
  const orbit = t >= 3 ? 0.2 : 0.16;
  let shards: THREE.Group | undefined;

  // II: a silver-trimmed capelet, a silver hood edge and silver prongs that cradle the crystal.
  if (t >= 2) {
    M(new THREE.CylinderGeometry(0.17, 0.275, 0.24, 12, 1, true), std('#3b2f7a', { side: THREE.DoubleSide, roughness: 0.95 }), b).position.y = 1.37;
    ring(b, 0.275, 0.012, trim, 1.25, 4, 24);
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2;
      limb(staff, V(0.33, top - 0.04, 0.14), V(0.33 + Math.cos(a) * 0.075, cy + 0.02, 0.14 + Math.sin(a) * 0.075), 0.008, 0.006, trim, 4);
    }
    ring(staff, 0.03, 0.01, trim, top - 0.06, 4, 10).position.set(0.33, top - 0.06, 0.14);
  }
  // III: a high standing collar, silver stars around the hem and a sash.
  if (t >= 3) {
    M(new THREE.CylinderGeometry(0.22, 0.17, 0.3, 12, 1, true, Math.PI - 1.2, 2.4), dark, b).position.y = 1.6;
    backArc(b, 0.22, 0.01, trim, 1.75, 2.4);
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      const st = M(new THREE.OctahedronGeometry(0.028), trim, f.fig);
      st.position.set(Math.sin(a) * 0.312, 0.14, Math.cos(a) * 0.312);
      st.rotation.y = a;
      st.scale.set(1, 1.3, 0.4);
    }
    limb(b, V(0.17, 1.42, 0.15), V(-0.16, 0.98, 0.15), 0.02, 0.02, cloth('#5a1f3a'), 4);
  }
  // IV: a ring of runes floats behind the head and the hands smoulder with arcane light.
  if (t >= 4) {
    // A crown of arcane shards floats over the hood.
    shards = G(h, 0, 1.95, -0.02);
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2;
      const sh = M(new THREE.OctahedronGeometry(0.035), crystal, shards);
      sh.position.set(Math.cos(a) * 0.17, (i % 2) * 0.03, Math.sin(a) * 0.17);
      sh.scale.y = 2.0;
      sh.rotation.z = Math.cos(a) * 0.35;
      sh.rotation.x = -Math.sin(a) * 0.35;
    }
    ring(shards, 0.17, 0.005, runeM, -0.06, 3, 28);
    glowSprite(shards, '#7cc4ff', 0.6, V(0, 0, 0), 0.3);
    glowSprite(f.lh, '#7cc4ff', 0.28, V(0, 0, 0.02), 0.6);
    aura(c.g, '#5ab8ff');
  }
  const tick = (n: number): void => {
    cr.rotation.y = n * 0.4;
    if (shards) shards.rotation.y = -n * 0.12;
    runes.forEach((r, i) => {
      const a = n * 0.35 + (i * Math.PI * 2) / runes.length;
      r.position.set(0.33 + Math.cos(a) * orbit, cy + Math.sin(a * 1.3) * 0.05, 0.14 + Math.sin(a) * orbit);
      r.rotation.y = a;
    });
  };
  tick(0);
  return hero(c, f, o, t >= 3 ? 2.4 : 2.2, tick);
};

export const ranger = (o?: HeroOpts): Model => {
  const c = begin(o, 0.62, 4);
  const t = o?.tier ?? 1;
  const lea = leather('#5a4632');
  const f = figure({ legs: cloth('#3a3226'), boots: leather('#2a1d12'), torso: lea, arms: cloth('#3d4a2e'), forearms: lea, cape: '#2a3f24', ...tierCape(t) });
  const b = f.bodyAbs, h = headgear(f.headAbs);
  const hood = M(new THREE.SphereGeometry(0.145, 12, 9, 0, Math.PI * 2, 0, Math.PI / 1.6), cloth('#2a3f24'), h);
  hood.position.set(0, 1.67, -0.02);
  hood.rotation.x = -0.3;
  const tip = M(new THREE.ConeGeometry(0.06, 0.18, 7), cloth('#2a3f24'), h);
  tip.position.set(0, 1.72, -0.16);
  tip.rotation.x = -2.0;
  // The longbow grows into a horn-tipped war bow.
  const bs = t >= 3 ? 1.15 : 1;
  const pts: THREE.Vector3[] = [];
  for (let i = 0; i <= 16; i++) {
    const a = -1.15 + (i / 16) * 2.3;
    const recurve = t >= 3 ? Math.pow(Math.abs(Math.sin(a)), 4) * 0.08 : 0;
    pts.push(V(-0.04, Math.sin(a) * 0.62 * bs, (Math.cos(a) * 0.22 - 0.18) * bs + recurve));
  }
  const bow = G(f.lh);
  M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 20, t >= 2 ? 0.018 : 0.015, 5), DWOOD, bow);
  limb(bow, pts[0]!, pts[16]!, 0.003, 0.003, STRING, 3);
  bow.rotation.y = 0.2;
  const quiver = M(new THREE.CylinderGeometry(0.06, 0.055, 0.5, 8), leather('#4a3320'), b);
  quiver.position.set(0.12, 1.25, -0.2);
  quiver.rotation.z = -0.4;
  const fm = cloth('#8a2a2a');
  const arrows = t >= 2 ? 6 : 4;
  for (let i = 0; i < arrows; i++) {
    const fl = M(new THREE.ConeGeometry(0.025, 0.07, 4), fm, b);
    fl.position.set(0.21 + (i % 4) * 0.02, 1.5 + (i >> 2) * 0.02, -0.2 + c.R() * 0.04 + (i >> 2) * 0.03);
    fl.rotation.z = -0.4;
  }

  // II: a leather pauldron on the bow arm, wide bracers and a grip wrap on the bow.
  if (t >= 2) {
    const dk = leather('#3e2e1e');
    pauldron(b, -1, dk, 1.15, t >= 3 ? 2 : 1, DSTEEL);
    // A studded jerkin over the tunic.
    M(new THREE.CylinderGeometry(0.218, 0.172, 0.42, 10), dk, b).position.y = 1.24;
    for (let i = 0; i < 8; i++) {
      const y = 1.4 - (i >> 1) * 0.1, k = i % 2 ? 1 : -1;
      const r = 0.216 - (1.45 - y) * 0.09;
      M(new THREE.SphereGeometry(0.014, 5, 3), DSTEEL, b).position.set(k * 0.07, y, Math.sqrt(r * r - 0.0049) + 0.004);
    }
    for (const [k, arm] of [[-1, f.armLAbs], [1, f.armRAbs]] as const) {
      limb(arm, V(k * 0.272, 1.06, 0.065), V(k * 0.28, 0.96, 0.11), 0.058, 0.06, dk, 8);
    }
    limb(bow, V(-0.04, -0.06, 0.04 * bs), V(-0.04, 0.06, 0.04 * bs), 0.024, 0.024, LEATHER_BELT, 6);
  }
  // III: a wolf-fur mantle, a bandolier of knives and horn tips on the bow.
  if (t >= 3) {
    const fur = std('#6a6157', { roughness: 1, bumpScale: 0.035 });
    for (let i = 0; i < 11; i++) {
      const a = Math.PI * 0.5 + (i / 10) * Math.PI;
      const tuft = M(new THREE.ConeGeometry(0.075, 0.26, 5), fur, b);
      tuft.position.set(Math.sin(a) * 0.21, 1.42, Math.cos(a) * 0.17);
      tuft.rotation.set(Math.cos(a) * 2.5, 0, -Math.sin(a) * 2.5);
    }
    const ruff = M(new THREE.TorusGeometry(0.17, 0.07, 5, 14), fur, b);
    ruff.rotation.x = Math.PI / 2;
    ruff.position.set(0, 1.47, -0.02);
    limb(b, V(-0.19, 1.43, 0.13), V(0, 1.2, 0.205), 0.022, 0.022, LEATHER_BELT, 4);
    limb(b, V(0, 1.2, 0.205), V(0.17, 0.98, 0.17), 0.022, 0.022, LEATHER_BELT, 4);
    for (let i = 0; i < 3; i++) {
      const p = V(-0.12 + i * 0.09, 1.34 - i * 0.11, 0.22);
      const kn = M(new THREE.BoxGeometry(0.02, 0.08, 0.02), STEEL, b);
      kn.position.copy(p);
      kn.rotation.z = 0.68;
    }
    for (const p of [pts[0]!, pts[16]!]) {
      const horn = M(new THREE.ConeGeometry(0.02, 0.09, 5), BONE, bow);
      horn.position.copy(p);
      horn.rotation.x = p.y > 0 ? 0.4 : Math.PI - 0.4;
    }
  }
  // IV: green runes wake along the bow, two hawk feathers on the hood and a forest-green aura.
  if (t >= 4) {
    const rune = emis('#e6ffd6', '#6fdc4a', 1.3);
    for (const i of [2, 4, 6, 10, 12, 14]) {
      const p = pts[i]!;
      M(new THREE.BoxGeometry(0.026, 0.035, 0.03), rune, bow).position.set(p.x, p.y, p.z);
    }
    glowSprite(bow, '#8cf06a', 0.24, pts[1]!.clone(), 0.55);
    glowSprite(bow, '#8cf06a', 0.24, pts[15]!.clone(), 0.55);
    for (const k of [-1, 1]) {
      const fe = M(new THREE.ConeGeometry(0.025, 0.22, 4), k < 0 ? fm : std('#e8e4dc', { roughness: 0.9 }), h);
      fe.position.set(0.1 + k * 0.02, 1.8, -0.08);
      fe.rotation.set(-0.5, 0, -0.5 + k * 0.15);
      fe.scale.z = 0.35;
    }
    aura(c.g, '#7fe06a');
  }
  return hero(c, f, o, t >= 3 ? 1.9 : 1.8);
};

/**
 * Battle-cleric (healer): layered white robes over a sapphire under-robe, silver plate
 * pauldrons, a silver circlet, a censer swinging from the off hand, a prayer book at the hip and a tall
 * staff crowned by a radiant sun-disc. Bright and vertical, so it reads as "the healer" at bastion zoom.
 */
export const cleric = (o?: HeroOpts): Model => {
  const c = begin(o, 0.62, 5);
  const tier = o?.tier ?? 1;
  const robe = cloth('#e4e6eb'), robeShade = cloth('#c3c8d2'), sapphire = cloth('#203a86');
  const silver = metal('#d9dee6', { roughness: 0.26 });
  const pewter = metal('#7d8794', { roughness: 0.5 });
  const holy = emis('#ffffff', '#cfe6ff', 1.6);
  const holySoft = emis('#eef6ff', '#9fd0ff', 0.9, { transparent: true, opacity: 0.6, side: THREE.DoubleSide, depthWrite: false });
  const f = figure({ legs: robeShade, boots: leather('#2a2320'), torso: robe, arms: robe, hands: SKIN, cape: '#dde0e7', ...tierCape(tier) });
  const b = f.bodyAbs, h = f.headAbs, fig = f.fig, hg = headgear(h);
  const TAU = Math.PI * 2;

  // Robes hang from the hips (in the figure, not the legs): a sapphire under-robe seen through the front slit of
  // the white outer robe, whose hem and slit are piped in silver.
  M(new THREE.CylinderGeometry(0.17, 0.335, 0.9, 12, 1, true), std('#203a86', { side: THREE.DoubleSide, roughness: 0.95, bumpScale: 0.02 }), fig).position.y = 0.47;
  const SLIT = 0.36;
  const outer = M(new THREE.CylinderGeometry(0.188, 0.375, 0.88, 14, 1, true, SLIT, TAU - SLIT * 2), std('#e4e6eb', { side: THREE.DoubleSide, roughness: 0.95, bumpScale: 0.02 }), fig);
  outer.position.y = 0.48;
  const hem = M(new THREE.CylinderGeometry(0.37, 0.382, 0.07, 14, 1, true, SLIT, TAU - SLIT * 2), std('#203a86', { side: THREE.DoubleSide, roughness: 0.9 }), fig);
  hem.position.y = 0.075;
  for (const k of [-1, 1]) {
    const a = k * SLIT;
    limb(fig, V(Math.sin(a) * 0.19, 0.92, Math.cos(a) * 0.19), V(Math.sin(a) * 0.378, 0.04, Math.cos(a) * 0.378), 0.008, 0.01, silver, 5);
  }
  // A few soft folds break the cone of the robe.
  for (let i = 0; i < 5; i++) {
    const a = SLIT + 0.45 + (i / 4) * (TAU - SLIT * 2 - 0.9);
    limb(fig, V(Math.sin(a) * 0.192, 0.8, Math.cos(a) * 0.192), V(Math.sin(a) * 0.372, 0.1, Math.cos(a) * 0.372), 0.008, 0.024, robe, 4);
  }

  // Sapphire tabard on the chest with a silver sun.
  const tab = M(new THREE.BoxGeometry(0.2, 0.44, 0.016), sapphire, b);
  tab.position.set(0, 1.235, 0.193);
  tab.rotation.x = 0.07;
  for (const k of [-1, 1]) {
    const e = M(new THREE.BoxGeometry(0.018, 0.44, 0.02), silver, b);
    e.position.set(k * 0.1, 1.235, 0.195);
    e.rotation.x = 0.07;
  }
  const sun = G(b, 0, 1.25, 0.21);
  sun.rotation.x = 0.07;
  M(new THREE.TorusGeometry(0.045, 0.01, 3, 12), silver, sun);
  M(new THREE.CircleGeometry(0.035, 12), holy, sun).position.z = 0.002;
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * TAU;
    const ray = M(new THREE.ConeGeometry(0.012, i % 2 ? 0.04 : 0.065, 4), silver, sun);
    const d = i % 2 ? 0.07 : 0.082;
    ray.position.set(Math.cos(a) * d, Math.sin(a) * d, 0);
    ray.rotation.z = a - Math.PI / 2;
  }

  // Rounded shoulders, silver pauldrons and a sapphire collar that runs into the tabard.
  const yoke = M(new THREE.SphereGeometry(0.222, 14, 4, 0, TAU, 0, Math.PI / 2), robe, b);
  yoke.position.y = 1.455;
  yoke.scale.y = 0.42;
  for (const k of [-1, 1]) {
    const pa = G(b, k * 0.25, 1.43, 0);
    pa.rotation.z = -k * 0.62;
    pa.scale.setScalar(tier >= 3 ? 1.22 : tier >= 2 ? 1.1 : 1);
    if (tier >= 2) {
      // II: a second lame and a sapphire-enamelled rim.
      const l2 = M(new THREE.SphereGeometry(0.1, 10, 2, 0, TAU, Math.PI / 2 - 0.5, 0.5), silver, pa);
      l2.position.y = -0.085;
      l2.scale.set(1.2, 0.9, 1.28);
      const rim = M(new THREE.TorusGeometry(0.11, 0.011, 3, 16), sapphire, pa);
      rim.rotation.x = Math.PI / 2;
      rim.scale.set(1.05, 1.15, 1);
    }
    const dome = M(new THREE.SphereGeometry(0.105, 10, 4, 0, TAU, 0, Math.PI / 2), silver, pa);
    dome.scale.set(1.05, 0.62, 1.15);
    const lame = M(new THREE.SphereGeometry(0.1, 10, 2, 0, TAU, Math.PI / 2 - 0.5, 0.5), silver, pa);
    lame.position.y = -0.045;
    lame.scale.set(1.12, 0.9, 1.2);
  }
  const cowl = M(new THREE.TorusGeometry(0.135, 0.045, 4, 14), sapphire, b);
  cowl.rotation.x = Math.PI / 2 - 0.12;
  cowl.position.set(0, 1.525, -0.01);
  cowl.scale.z = 0.8;

  // Head: short dark hair, a silver circlet with a small glowing stone.
  const hair = M(new THREE.SphereGeometry(0.128, 10, 5, 0, TAU, 0, Math.PI / 2.1), std('#3b2a1f', { roughness: 0.9 }), h);
  hair.position.set(0, 1.64, -0.012);
  hair.rotation.x = -0.32;
  const circ = M(new THREE.TorusGeometry(0.124, 0.011, 3, 16), silver, hg);
  circ.position.set(0, 1.68, 0);
  circ.rotation.x = Math.PI / 2 - 0.12;
  const gem = M(new THREE.OctahedronGeometry(0.018), holy, hg);
  gem.position.set(0, 1.695, 0.126);
  gem.scale.y = 1.5;
  glowSprite(hg, '#cfe6ff', 0.12, V(0, 1.695, 0.135), 0.7);

  // A sun of the dawn stitched on the back of the cape.
  const back = G(b, 0, 1.12, -0.305);
  back.rotation.y = Math.PI;
  back.rotation.x = 0.15;
  M(new THREE.TorusGeometry(0.06, 0.011, 3, 12), sapphire, back);
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * TAU;
    const ray = M(new THREE.BoxGeometry(0.012, 0.045, 0.006), sapphire, back);
    ray.position.set(Math.cos(a) * 0.09, Math.sin(a) * 0.09, 0);
    ray.rotation.z = a - Math.PI / 2;
  }

  // Bell sleeves with silver cuffs.
  for (const [k, arm] of [[-1, f.armLAbs], [1, f.armRAbs]] as const) {
    limb(arm, V(k * 0.268, 1.12, 0.05), V(k * 0.283, 0.955, 0.117), 0.058, 0.088, robe, 8);
    limb(arm, V(k * 0.282, 0.968, 0.113), V(k * 0.285, 0.944, 0.122), 0.093, 0.093, sapphire, 8);
  }

  // Prayer book on the left hip, on a short chain.
  const book = G(b, -0.19, 0.84, -0.09);
  book.rotation.set(0.1, -1.9, 0.1);
  M(new THREE.BoxGeometry(0.13, 0.16, 0.05), leather('#5e1c22'), book);
  M(new THREE.BoxGeometry(0.115, 0.15, 0.052), std('#e4e0d6', { roughness: 0.9 }), book).position.x = 0.01;
  for (const [x, y] of [[-0.055, 0.07], [-0.055, -0.07]] as const) M(new THREE.BoxGeometry(0.03, 0.03, 0.056), silver, book).position.set(x, y, 0);
  M(new THREE.BoxGeometry(0.02, 0.05, 0.058), silver, book).position.set(0.06, 0, 0);
  limb(b, V(-0.16, 0.98, -0.07), V(-0.19, 0.92, -0.09), 0.005, 0.005, silver, 4);

  // The staff: dark wood with silver bands, crowned by a radiant sun-disc.
  const staff = G(f.rhAbs);
  const SX = 0.3, SZ = 0.14, TOP = 1.98;
  limb(staff, V(SX, 0.02, SZ), V(SX, TOP - 0.1, SZ), 0.019, 0.024, DWOOD, 7);
  for (const y of [0.04, 0.82, 1.02, 1.62]) M(new THREE.CylinderGeometry(0.028, 0.028, 0.035, 6), silver, staff).position.set(SX, y, SZ);
  M(new THREE.CylinderGeometry(0.022, 0.035, 0.1, 8), silver, staff).position.set(SX, TOP - 0.12, SZ);
  const head = G(staff, SX, TOP + 0.1, SZ);
  head.scale.setScalar(tier >= 4 ? 1.3 : tier >= 3 ? 1.18 : 1);
  const disc = G(head);
  M(new THREE.TorusGeometry(0.15, 0.019, 4, 24), silver, disc);
  M(new THREE.TorusGeometry(0.095, 0.009, 3, 18), silver, disc);
  M(new THREE.CircleGeometry(0.142, 18), holySoft, disc);
  M(new THREE.SphereGeometry(0.05, 8, 6), holy, disc);
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * TAU;
    const long = i % 2 === 0;
    const len = long ? 0.12 : 0.065;
    const ray = M(new THREE.ConeGeometry(long ? 0.02 : 0.013, len, 4), long ? silver : holy, disc);
    const d = 0.165 + len / 2;
    ray.position.set(Math.cos(a) * d, Math.sin(a) * d, 0);
    ray.rotation.z = a - Math.PI / 2;
    ray.scale.z = 0.5;
  }
  // The neck that holds the disc.
  limb(staff, V(SX, TOP - 0.07, SZ), V(SX, TOP - 0.06 + 0.04, SZ), 0.03, 0.02, silver, 8);
  const halo = glowSprite(head, '#bcdcff', 1.05, V(0, 0, 0), 0.55, true);
  glowSprite(head, '#ffffff', 0.34, V(0, 0, 0.02), 0.95);
  pointLight(c, 0xdbe9ff, 0.35, 2.2, V(SX, TOP + 0.1, SZ + 0.1));

  // Censer swinging from the off hand on three chains, embers glowing through its lid.
  const swing = G(f.lh);
  const cen = G(swing, 0, -0.34, 0.02);
  cen.scale.setScalar(1.25);
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * TAU;
    limb(swing, V(0, -0.02, 0), V(Math.cos(a) * 0.045, -0.3, 0.02 + Math.sin(a) * 0.045), 0.0035, 0.0035, silver, 3);
  }
  M(new THREE.SphereGeometry(0.06, 10, 3, 0, TAU, Math.PI / 2, Math.PI / 2), silver, cen);
  M(new THREE.CylinderGeometry(0.061, 0.061, 0.016, 10, 1, true), emis('#ffd3a0', '#ff9a4a', 1.4), cen);
  const lid = M(new THREE.ConeGeometry(0.058, 0.07, 10, 1, true), pewter, cen);
  lid.position.y = 0.04;
  M(new THREE.SphereGeometry(0.014, 5, 3), silver, cen).position.y = 0.08;
  M(new THREE.CylinderGeometry(0.02, 0.03, 0.03, 8), silver, cen).position.y = -0.065;
  const ember = glowSprite(cen, '#ffb06a', 0.36, V(0, 0.01, 0), 0.8, true);

  // II: a silver girdle with a sapphire stole hanging from it, and silver vambraces.
  if (tier >= 2) {
    ring(b, 0.172, 0.018, silver, 0.97, 4, 16);
    for (const k of [-1, 1]) {
      const st = M(new THREE.BoxGeometry(0.06, 0.42, 0.012), sapphire, fig);
      st.position.set(k * 0.075, 0.74, 0.255);
      st.rotation.x = 0.2;
      M(new THREE.BoxGeometry(0.064, 0.025, 0.016), silver, fig).position.set(k * 0.075, 0.535, 0.296);
    }
    for (const [k, arm] of [[-1, f.armLAbs], [1, f.armRAbs]] as const) limb(arm, V(k * 0.272, 1.2, 0.03), V(k * 0.27, 1.13, 0.04), 0.06, 0.06, silver, 8);
  }
  // III: the circlet rises into a crown of silver rays, and a sapphire mantle covers the shoulders.
  if (tier >= 3) {
    for (let i = 0; i < 7; i++) {
      const a = ((i - 3) / 3) * 1.1;
      const ray = M(new THREE.ConeGeometry(0.014, i === 3 ? 0.11 : i % 2 ? 0.06 : 0.08, 4), silver, hg);
      ray.position.set(Math.sin(a) * 0.124, 1.71 + (i === 3 ? 0.03 : 0.015), Math.cos(a) * 0.12);
      ray.rotation.set(Math.cos(a) * 0.25, 0, -Math.sin(a) * 0.25);
    }
    const mantle = M(new THREE.CylinderGeometry(0.2, 0.33, 0.26, 14, 1, true, Math.PI - 1.4, 2.8), std('#203a86', { side: THREE.DoubleSide, roughness: 0.95, bumpScale: 0.02 }), b);
    mantle.position.y = 1.36;
    backArc(b, 0.33, 0.012, silver, 1.23, 2.8);
  }
  // IV: a halo of dawn behind the head and a pale aura.
  if (tier >= 4) {
    const ha = G(h, 0, 1.74, -0.16);
    ha.scale.setScalar(1.15);
    M(new THREE.TorusGeometry(0.2, 0.012, 3, 28), holy, ha);
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * TAU;
      const ray = M(new THREE.ConeGeometry(0.01, i % 2 ? 0.05 : 0.08, 3), holy, ha);
      const d = 0.215 + (i % 2 ? 0.025 : 0.04);
      ray.position.set(Math.cos(a) * d, Math.sin(a) * d, 0);
      ray.rotation.z = a - Math.PI / 2;
    }
    glowSprite(ha, '#dcecff', 0.75, V(0, 0, -0.02), 0.3);
    aura(c.g, '#cfe6ff');
  }

  const t = (n: number): void => {
    const breath = 0.5 + 0.5 * Math.sin(n * 0.35);
    (halo.material as THREE.SpriteMaterial).opacity = 0.42 + 0.22 * breath;
    halo.scale.setScalar(0.95 + 0.18 * breath);
    disc.rotation.z = n * 0.03;
    swing.rotation.set(Math.sin(n * 0.42) * 0.12, 0, Math.sin(n * 0.42 + 1.2) * 0.22);
    (ember.material as THREE.SpriteMaterial).opacity = 0.6 + 0.25 * ((n * 7919) % 5) / 4;
  };
  t(0);
  return hero(c, f, o, 2.2, t);
};

/* ---------- barbarian, paladin, druid (and the druid's bear form) ---------- */

/** A flat ring arc of `arc` radians centered on the front (+Z) at height y (war paint, gorget plates). */
const frontArc = (parent: THREE.Object3D, r: number, tube: number, mat: THREE.Material, y: number, arc: number): THREE.Mesh => {
  const m = backArc(parent, r, tube, mat, y, arc);
  m.parent!.rotation.y += Math.PI;
  return m;
};

/** The pivot's own frame (abs group) of a leg: accessories placed in figure coordinates follow the leg. */
const legAbs = (leg: THREE.Group): THREE.Group => leg.children[0] as THREE.Group;

/**
 * Bearded axe blade in the XY plane (cutting edge toward +X, haft along Y at the origin), `w` long and `h` tall:
 * held flat to the table camera, like the warrior's sword, so the blade reads. `back` mirrors it across the
 * haft (the smaller second bit of a double axe).
 */
const axeBlade = (w: number, h: number, mat: THREE.Material, back = false): THREE.Mesh => {
  const s = new THREE.Shape();
  s.moveTo(0.015, h * 0.16);
  s.quadraticCurveTo(w * 0.45, h * 0.2, w * 0.85, h * 0.5);
  s.quadraticCurveTo(w * 1.12, 0, w * 0.85, -h * 0.5);
  s.quadraticCurveTo(w * 0.42, -h * 0.28, 0.015, -h * 0.2);
  s.closePath();
  const geo = new THREE.ExtrudeGeometry(s, { depth: 0.022, bevelEnabled: true, bevelThickness: 0.006, bevelSize: 0.006, bevelSegments: 1, curveSegments: 5 });
  geo.translate(0, 0, -0.011);
  const m = M(geo, mat);
  if (back) m.rotation.y = Math.PI;
  return m;
};

/**
 * Barbarian: bare, broad and fast. Bare arms and chest under a fur kilt and wolf-pelt mantle, two long
 * braids and a braided beard, red war paint across the eyes and the arms, and a great bearded axe held high.
 * The broadest silhouette of the heroes, with the biggest blade: it reads as "the one who goes out hunting".
 */
export const barbarian = (o?: HeroOpts): Model => {
  const c = begin(o, 0.62, 6);
  const t = o?.tier ?? 1;
  const hide = leather('#3e2c1f'), hideDk = leather('#2b1f16');
  const fur = std('#5c4a3a', { roughness: 1, bumpScale: 0.035 }), furDk = std('#3a2e25', { roughness: 1, bumpScale: 0.035 });
  const furGrey = std('#6d6359', { roughness: 1, bumpScale: 0.035 });
  const hair = std('#2a1f18', { roughness: 0.9 });
  const paint = cloth('#a3221c');
  const blade = metal('#8e959e', { roughness: 0.32 });
  const f = figure({ legs: hide, boots: furDk, torso: SKIN, shoulders: SKIN, arms: SKIN, forearms: SKIN, hands: SKIN });
  const b = f.bodyAbs, h = f.headAbs, fig = f.fig, hg = headgear(h);
  fig.scale.set(1.08, 1.03, 1.08);

  // A heavy chest and shoulders over the plain torso; thick upper arms, leather bracers and big hands.
  M(new THREE.CylinderGeometry(0.245, 0.18, 0.44, 10), SKIN, b).position.y = 1.24;
  for (const k of [-1, 1]) {
    const pec = M(new THREE.SphereGeometry(0.1, 8, 5), SKIN, b);
    pec.position.set(k * 0.09, 1.35, 0.17);
    pec.scale.set(1.05, 0.72, 0.5);
    const delt = M(new THREE.SphereGeometry(0.105, 9, 6), SKIN, b);
    delt.position.set(k * 0.255, 1.41, 0);
    delt.scale.set(1, 0.9, 1.05);
  }
  for (const [k, arm] of [[-1, f.armLAbs], [1, f.armRAbs]] as const) {
    limb(arm, V(k * 0.245, 1.36, 0), V(k * 0.272, 1.15, 0.04), 0.07, 0.058, SKIN, 8);
    limb(arm, V(k * 0.274, 1.09, 0.06), V(k * 0.283, 0.965, 0.112), 0.058, 0.056, hideDk, 8);
    M(new THREE.SphereGeometry(0.056, 7, 5), SKIN, arm).position.set(k * 0.28, 0.91, 0.13);
    // War paint: two red bands round the upper arm.
    for (const y of [1.29, 1.24]) limb(arm, V(k * 0.254, y + 0.012, 0.014), V(k * 0.257, y - 0.012, 0.018), 0.0715, 0.0705, paint, 8);
  }

  // Wide belt with an iron buckle, a fur kilt hanging from the hips (in the figure, not the legs) and fur boot wraps.
  ring(b, 0.19, 0.04, hideDk, 0.98, 5, 14);
  M(new THREE.BoxGeometry(0.09, 0.08, 0.03), DSTEEL, b).position.set(0, 0.98, 0.215);
  M(new THREE.CylinderGeometry(0.2, 0.27, 0.3, 10), fur, fig).position.y = 0.8;
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2 + 0.3;
    const tuft = M(new THREE.ConeGeometry(0.06, 0.16, 4), i % 2 ? fur : furDk, fig);
    tuft.position.set(Math.sin(a) * 0.255, 0.62, Math.cos(a) * 0.255);
    tuft.rotation.set(Math.PI - Math.cos(a) * 0.25, 0, Math.sin(a) * 0.25);
  }
  for (const leg of [f.rig.legL, f.rig.legR]) {
    const k = leg === f.rig.legL ? -1 : 1;
    limb(legAbs(leg), V(k * 0.09, 0.14, 0.01), V(k * 0.095, 0.4, 0), 0.085, 0.075, furDk, 7);
  }
  // A leather strap across the chest.
  limb(b, V(0.18, 1.45, 0.14), V(0, 1.22, 0.235), 0.022, 0.022, hideDk, 4);
  limb(b, V(0, 1.22, 0.235), V(-0.18, 1.0, 0.17), 0.022, 0.022, hideDk, 4);

  // Head: wild dark hair, two long braids with iron rings, a braided beard and red paint across the eyes.
  const mane = M(new THREE.SphereGeometry(0.135, 10, 6, 0, Math.PI * 2, 0, Math.PI / 1.9), hair, h);
  mane.position.set(0, 1.645, -0.02);
  mane.rotation.x = -0.35;
  // The topknot hides under a worn helm or hood (models/dress.ts).
  const knot = M(new THREE.ConeGeometry(0.06, 0.16, 6), hair, h);
  knot.position.set(0, 1.79, -0.06);
  knot.name = 'topknot';
  for (const k of [-1, 1]) {
    limb(h, V(k * 0.105, 1.63, 0.03), V(k * 0.13, 1.46, 0.08), 0.03, 0.026, hair, 5);
    limb(h, V(k * 0.13, 1.46, 0.08), V(k * 0.135, 1.32, 0.12), 0.026, 0.018, hair, 5);
    ring(h, 0.03, 0.009, DSTEEL, 1.455, 3, 8).position.set(k * 0.13, 1.455, 0.08);
  }
  const beard = M(new THREE.ConeGeometry(0.075, 0.24, 6), hair, h);
  beard.position.set(0, 1.47, 0.1);
  beard.rotation.x = Math.PI + 0.25;
  ring(h, 0.03, 0.008, DSTEEL, 1.42, 3, 8).position.set(0, 1.42, 0.115);
  frontArc(h, 0.121, 0.017, paint, 1.645, 2.0);

  // The great axe: a long haft held high, a bearded blade (II on: double-bitted), a spike on top.
  const axe = G(f.rh);
  axe.rotation.set(0.3, 0, -0.1);
  const as = [1, 1.12, 1.25, 1.35][t - 1]!;
  const top = 0.82 + 0.06 * (t - 1);
  limb(axe, V(0, -0.3, 0), V(0, top + 0.06, 0), 0.022, 0.024, DWOOD, 6);
  limb(axe, V(0, -0.06, 0), V(0, 0.1, 0), 0.027, 0.027, hideDk, 6);
  M(new THREE.SphereGeometry(0.03, 6, 4), DSTEEL, axe).position.y = -0.31;
  M(new THREE.BoxGeometry(0.05, 0.16 * as, 0.06), DSTEEL, axe).position.y = top - 0.05;
  const bl = axeBlade(0.3 * as, 0.38 * as, blade);
  bl.position.y = top - 0.05;
  axe.add(bl);
  M(new THREE.ConeGeometry(0.022, 0.12, 4), DSTEEL, axe).position.y = top + 0.1;

  // II: a wolf-pelt mantle, studded iron bracers, a necklace of fangs and a second, smaller bit on the axe.
  if (t >= 2) {
    const ruff = M(new THREE.TorusGeometry(0.19, 0.07, 5, 14), furGrey, b);
    ruff.rotation.x = Math.PI / 2;
    ruff.position.set(0, 1.47, -0.03);
    ruff.scale.set(1.2, 1, 1);
    for (let i = 0; i < 9; i++) {
      const a = Math.PI * 0.55 + (i / 8) * Math.PI * 0.9;
      const tuft = M(new THREE.ConeGeometry(0.08, 0.3, 5), i % 2 ? furGrey : fur, b);
      tuft.position.set(Math.sin(a) * 0.23, 1.38, Math.cos(a) * 0.19);
      tuft.rotation.set(Math.cos(a) * 2.6, 0, -Math.sin(a) * 2.6);
    }
    for (const [k, arm] of [[-1, f.armLAbs], [1, f.armRAbs]] as const) {
      for (const y of [1.06, 0.99]) ring(arm, 0.061, 0.01, DSTEEL, y, 3, 10).position.set(k * 0.278, y, 0.08 + (1.06 - y) * 0.4);
    }
    for (let i = 0; i < 7; i++) {
      const a = ((i - 3) / 3) * 0.9;
      const fang = M(new THREE.ConeGeometry(0.013, 0.05, 4), BONE, b);
      fang.position.set(Math.sin(a) * 0.15, 1.4 - Math.abs(i - 3) * 0.008, Math.cos(a) * 0.19);
      fang.rotation.x = Math.PI;
    }
    const bk = axeBlade(0.18 * as, 0.26 * as, blade, true);
    bk.position.y = top - 0.05;
    axe.add(bk);
  }
  // III: a horned iron cap, a spiked pauldron on the off shoulder and a warlord's skull at the belt.
  if (t >= 3) {
    const cap = M(new THREE.SphereGeometry(0.138, 10, 5, 0, Math.PI * 2, 0, Math.PI / 2.4), DSTEEL, hg);
    cap.position.set(0, 1.66, -0.005);
    ring(hg, 0.133, 0.014, DSTEEL, 1.69, 3, 14);
    for (const k of [-1, 1]) {
      const pts = [V(k * 0.12, 1.72, 0), V(k * 0.24, 1.76, 0.02), V(k * 0.3, 1.88, 0.05), V(k * 0.27, 2.0, 0.08)];
      M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 8, 0.03, 5), BONE, hg);
      const tip = M(new THREE.ConeGeometry(0.03, 0.08, 5), BONE, hg);
      tip.position.set(k * 0.265, 2.03, 0.09);
      tip.rotation.z = k * 0.3;
    }
    const pa = pauldron(b, -1, DSTEEL, 1.3, 2, hideDk);
    for (let i = 0; i < 3; i++) {
      const sp = M(new THREE.ConeGeometry(0.018, 0.09, 4), blade, pa);
      sp.position.set(-0.02 + i * 0.04, 0.05, -0.04 + i * 0.04);
    }
    const sk = G(b, -0.2, 0.92, 0.13);
    sk.rotation.y = -0.6;
    const cr = M(new THREE.SphereGeometry(0.055, 8, 6), BONE, sk);
    cr.scale.set(0.9, 1, 1.05);
    M(new THREE.BoxGeometry(0.06, 0.03, 0.05), BONE, sk).position.set(0, -0.05, 0.015);
    for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.014, 5, 3), hair, sk).position.set(k * 0.02, 0.005, 0.05);
  }
  // IV: ember runes burn along the blade and the war paint smoulders; an ember-red aura.
  if (t >= 4) {
    const ember = emis('#ffd2c8', '#ff4f3a', 1.3);
    const bit = G(axe, 0, top - 0.05, 0);
    for (const z of [-0.019, 0.019]) runeRow(bit, ember, V(0.17 * as, 0.08 * as, z), V(0.17 * as, -0.08 * as, z), 4, 0.03, 0.035, 0.004);
    frontArc(h, 0.1225, 0.006, ember, 1.645, 1.6);
    for (const [k, arm] of [[-1, f.armLAbs], [1, f.armRAbs]] as const) limb(arm, V(k * 0.265, 1.2, 0.03), V(k * 0.272, 1.15, 0.04), 0.062, 0.06, ember, 8);
    glowSprite(axe, '#ff6a3a', 0.42, V(0.14 * as, top - 0.05, 0), 0.5);
    aura(c.g, '#e0703a');
  }
  return hero(c, f, o, [1.93, 1.97, 2.14, 2.14][t - 1]!);
};

/**
 * Paladin: the heaviest armour. Boxy light-steel plate under a red tabard with a white cross, a closed
 * great helm, a red cloak, a tall white shield bearing the red cross, and a war hammer. Steel, white and
 * red only (no gold anywhere). Squarer than the warrior's round helm and kite shield.
 */
export const paladin = (o?: HeroOpts): Model => {
  const c = begin(o, 0.62, 7);
  const t = o?.tier ?? 1;
  const plate = metal('#b8c0ca', { roughness: 0.3 });
  const mail = metal('#6c727b', { roughness: 0.6 });
  const red = cloth('#8e1f22'), white = cloth('#d9dce3');
  const enamel = std('#d4d8de', { roughness: 0.5 });
  const slit = BLACK;
  const f = figure({ legs: plate, boots: DSTEEL, torso: plate, shoulders: plate, arms: mail, forearms: plate, hands: DSTEEL, cape: '#6e1f24', ...tierCape(t) });
  const b = f.bodyAbs, h = headgear(f.headAbs), fig = f.fig;
  fig.scale.set(1.04, 1, 1.04);

  // Boxy breastplate with a central ridge, a stack of faulds at the waist, and the red tabard over it all.
  M(new THREE.BoxGeometry(0.44, 0.34, 0.3), plate, b).position.set(0, 1.28, 0.005);
  const ridge = M(new THREE.BoxGeometry(0.04, 0.32, 0.04), plate, b);
  ridge.position.set(0, 1.28, 0.15);
  ridge.rotation.y = Math.PI / 4;
  for (let i = 0; i < 2; i++) M(new THREE.BoxGeometry(0.4 - i * 0.02, 0.06, 0.29 - i * 0.01), i ? mail : plate, b).position.y = 1.085 - i * 0.055;
  const tab = M(new THREE.BoxGeometry(0.24, 0.66, 0.016), red, b);
  tab.position.set(0, 1.05, 0.162);
  tab.rotation.x = -0.04;
  M(new THREE.BoxGeometry(0.24, 0.5, 0.016), red, b).position.set(0, 1.12, -0.16);
  M(new THREE.BoxGeometry(0.045, 0.2, 0.01), white, b).position.set(0, 1.24, 0.172);
  M(new THREE.BoxGeometry(0.14, 0.045, 0.01), white, b).position.set(0, 1.27, 0.172);
  ring(b, 0.2, 0.02, LEATHER_BELT, 1.02, 4, 14);

  // Square pauldrons over the shoulder balls.
  for (const k of [-1, 1]) {
    const pa = G(b, k * 0.265, 1.43, 0);
    pa.rotation.z = -k * 0.35;
    M(new THREE.BoxGeometry(0.17, 0.05, 0.22), plate, pa);
    M(new THREE.BoxGeometry(0.16, 0.05, 0.21), plate, pa).position.set(k * 0.02, -0.05, 0);
  }

  // Closed great helm: flat-topped, a dark eye slit, a reinforcing cross down the face.
  const helm = M(new THREE.CylinderGeometry(0.135, 0.13, 0.26, 10), plate, h);
  helm.position.y = 1.65;
  const crown = M(new THREE.CylinderGeometry(0.09, 0.135, 0.05, 10), plate, h);
  crown.position.y = 1.805;
  M(new THREE.BoxGeometry(0.2, 0.024, 0.05), slit, h).position.set(0, 1.67, 0.115);
  M(new THREE.BoxGeometry(0.035, 0.2, 0.03), plate, h).position.set(0, 1.62, 0.132);
  for (let i = 0; i < 3; i++) M(new THREE.BoxGeometry(0.014, 0.014, 0.03), slit, h).position.set(0.055, 1.6 - i * 0.03, 0.125);

  // The tall white shield with a red cross and a steel rim.
  const sw0 = [0.5, 0.52, 0.56, 0.58][t - 1]!, sh0 = [0.68, 0.7, 0.74, 0.76][t - 1]!;
  const sh = heaterShield(sw0, sh0, enamel);
  f.lh.add(sh);
  sh.position.set(-0.07, 0.06, 0.02);
  sh.rotation.y = -0.35;
  const rim = heaterShield(sw0 + 0.04, sh0 + 0.04, plate);
  sh.add(rim);
  rim.position.z = -0.012;
  M(new THREE.BoxGeometry(0.07, sh0 * 0.78, 0.012), red, sh).position.set(0, -0.02, 0.054);
  M(new THREE.BoxGeometry(sw0 * 0.78, 0.07, 0.012), red, sh).position.set(0, sh0 * 0.14, 0.054);

  // The war hammer: a square steel head facing forward, a back spike, a red grip.
  const hammer = G(f.rh);
  hammer.rotation.set(0.35, 0, -0.1);
  const hl = [0.62, 0.66, 0.7, 0.74][t - 1]!, hs = [1, 1.1, 1.22, 1.32][t - 1]!;
  limb(hammer, V(0, -0.1, 0), V(0, hl + 0.04, 0), 0.02, 0.02, DWOOD, 6);
  limb(hammer, V(0, -0.06, 0), V(0, 0.09, 0), 0.025, 0.025, red, 6);
  M(new THREE.SphereGeometry(0.03, 6, 4), plate, hammer).position.y = -0.11;
  const head = G(hammer, 0, hl, 0);
  head.scale.setScalar(hs);
  M(new THREE.BoxGeometry(0.09, 0.09, 0.14), plate, head).position.z = 0.03;
  M(new THREE.BoxGeometry(0.105, 0.105, 0.03), plate, head).position.z = 0.1;
  const spike = M(new THREE.ConeGeometry(0.03, 0.12, 4), DSTEEL, head);
  spike.position.z = -0.09;
  spike.rotation.x = -Math.PI / 2;
  M(new THREE.ConeGeometry(0.02, 0.08, 4), DSTEEL, head).position.y = 0.08;

  // II: layered pauldrons with dark trim, a gorget, knee cops and steel bosses on the shield.
  if (t >= 2) {
    for (const k of [-1, 1]) pauldron(b, k, plate, t >= 3 ? 1.35 : 1.2, t >= 3 ? 3 : 2, DSTEEL);
    ring(b, 0.12, 0.035, plate, 1.47, 5, 14);
    for (const leg of [f.rig.legL, f.rig.legR]) {
      const k = leg === f.rig.legL ? -1 : 1;
      const cop = M(new THREE.SphereGeometry(0.07, 8, 5), plate, legAbs(leg));
      cop.position.set(k * 0.095, 0.5, 0.05);
      cop.scale.set(1, 0.9, 0.7);
    }
    M(new THREE.SphereGeometry(0.045, 8, 5), plate, sh).position.set(0, sh0 * 0.14, 0.07);
  }
  // III: a crown of steel points on the helm, a white-bordered tabard reaching lower, studs round the shield and a flanged hammer.
  if (t >= 3) {
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      M(new THREE.ConeGeometry(0.016, 0.07, 4), plate, h).position.set(Math.sin(a) * 0.11, 1.85, Math.cos(a) * 0.11);
    }
    for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.014, 0.66, 0.018), white, b).position.set(k * 0.12, 1.05, 0.165);
    const skirt = M(new THREE.BoxGeometry(0.22, 0.2, 0.014), red, fig);
    skirt.position.set(0, 0.62, 0.2);
    M(new THREE.BoxGeometry(0.22, 0.016, 0.018), white, fig).position.set(0, 0.52, 0.2);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      M(new THREE.SphereGeometry(0.013, 5, 3), DSTEEL, sh).position.set(Math.sin(a) * sw0 * 0.42, sh0 * 0.06 + Math.cos(a) * sh0 * 0.38, 0.05);
    }
    for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.015, 0.11, 0.12), plate, head).position.set(k * 0.06, 0, 0.02);
  }
  // IV: the cross on the shield and runes on the hammer burn white, and a pale steel aura.
  if (t >= 4) {
    const holy = emis('#ffffff', '#dfe8ff', 1.5);
    M(new THREE.BoxGeometry(0.03, sh0 * 0.66, 0.006), holy, sh).position.set(0, -0.02, 0.062);
    M(new THREE.BoxGeometry(sw0 * 0.66, 0.03, 0.006), holy, sh).position.set(0, sh0 * 0.14, 0.062);
    for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.004, 0.05, 0.08), holy, head).position.set(k * 0.047, 0, 0.03);
    glowSprite(sh, '#e8f0ff', 0.42, V(0, sh0 * 0.14, 0.1), 0.45);
    glowSprite(head, '#e8f0ff', 0.3, V(0, 0, 0.04), 0.5);
    aura(c.g, '#c9d3e6');
  }
  return hero(c, f, o, t >= 3 ? 1.9 : 1.84);
};

/** Antler on side k of a head frame: a beam out and up from (x0, y0) with `tines` points, `s` its size. */
const antler = (parent: THREE.Object3D, k: number, y0: number, s: number, tines: number, mat: THREE.Material): THREE.Vector3[] => {
  const p0 = V(k * 0.08, y0, -0.02);
  const p1 = V(k * (0.08 + 0.13 * s), y0 + 0.17 * s, -0.05);
  const p2 = V(k * (0.08 + 0.2 * s), y0 + 0.36 * s, -0.02);
  limb(parent, p0, p1, 0.022, 0.017, mat, 5);
  limb(parent, p1, p2, 0.017, 0.009, mat, 5);
  const tips: THREE.Vector3[] = [p2];
  for (let i = 0; i < tines; i++) {
    const u = (i + 1) / (tines + 1);
    const base = i % 2 ? p1.clone().lerp(p2, u) : p0.clone().lerp(p1, 0.4 + u * 0.6);
    const tip = base.clone().add(V(k * (i % 2 ? 0.05 : -0.02) * s, (0.1 + 0.03 * i) * s, (i % 2 ? 0.05 : 0.07) * s));
    limb(parent, base, tip, 0.011, 0.005, mat, 4);
    tips.push(tip);
  }
  return tips;
};

/**
 * Druid: tall and still. A long dark-green robe under a cloak shingled with leaves, a deep hood crowned with
 * antlers, bark bracers and a gnarled staff that curls round a mossy stone. Dark greens, bark and stone:
 * the antlers make the tallest, most branching head of the heroes.
 */
export const druid = (o?: HeroOpts): Model => {
  const c = begin(o, 0.62, 8);
  const t = o?.tier ?? 1;
  const robe = cloth('#2e3a26'), sleeve = cloth('#3a4a2e'), hood = cloth('#26331f');
  const bark = std('#4a3a2c', { roughness: 1, bumpScale: 0.04 });
  const moss = std('#5d7a3a', { roughness: 1, bumpScale: 0.04 });
  const leaves = [cloth('#3f5a2e'), cloth('#4d6b34'), cloth('#2f4a2a')];
  const horn = std('#a8977a', { roughness: 0.9 });
  const rock = stone('#7d7a72');
  const cape = tierCape(t);
  const f = figure({ legs: robe, boots: bark, torso: robe, arms: sleeve, forearms: sleeve, hands: SKIN, cape: '#26341f', ...cape });
  const b = f.bodyAbs, h = f.headAbs, fig = f.fig, hg = headgear(h);
  fig.scale.set(1, 1.04, 1);

  // Robe to the ground (in the figure, so it doesn't swing with the legs) and a rope belt with a stone.
  M(new THREE.ConeGeometry(0.35, 0.98, 12, 1, true), std('#2e3a26', { side: THREE.DoubleSide, roughness: 0.95, bumpScale: 0.02 }), fig).position.y = 0.5;
  ring(b, 0.172, 0.018, bark, 0.98, 4, 14);
  M(new THREE.DodecahedronGeometry(0.035, 0), rock, b).position.set(0.06, 0.95, 0.17);

  // Leaves shingled down the cloak, darker toward the hem.
  const leaf = (parent: THREE.Object3D, p: THREE.Vector3, a: number, i: number, s = 1, droop = 0.35): void => {
    const lg = G(parent, p.x, p.y, p.z);
    lg.rotation.y = a;
    const l = M(new THREE.ConeGeometry(0.045 * s, 0.13 * s, 4), leaves[i % 3]!, lg);
    l.rotation.x = Math.PI - droop;
    l.scale.z = 0.32;
  };
  const rows = 4 + Math.min(t, 3);
  for (let r = 0; r < rows; r++) {
    const s = (r + 0.4) / rows;
    const n = 5 + (r >> 1);
    for (let i = 0; i < n; i++) {
      const a = Math.PI + ((i + (r % 2) * 0.5) / n - 0.5) * cape.capeArc * 0.9;
      const rr = 0.24 + (cape.capeR - 0.24) * s + 0.012;
      leaf(b, V(Math.sin(a) * rr, 1.46 - s * cape.capeLen, Math.cos(a) * rr), a, r + i);
    }
  }

  // A collar of leaves round the neck, falling over the chest.
  for (let i = 0; i < 9; i++) {
    const a = ((i - 4) / 4) * 1.9;
    leaf(b, V(Math.sin(a) * 0.17, 1.47, Math.cos(a) * 0.17), a, i, 1, 0.45);
  }

  // The deep hood, edged with leaves, a shadowed face, and the antlers.
  const hd = M(new THREE.SphereGeometry(0.148, 12, 9, 0, Math.PI * 2, 0, Math.PI / 1.6), hood, hg);
  hd.position.set(0, 1.67, -0.025);
  hd.rotation.x = -0.32;
  for (let i = 0; i < 6; i++) {
    const a = ((i - 2.5) / 2.5) * 1.2;
    leaf(hg, V(Math.sin(a) * 0.14, 1.77 - Math.abs(a) * 0.06, Math.cos(a) * 0.1), a, i, 0.8, -0.6);
  }
  const as = [1, 1.18, 1.34, 1.45][t - 1]!;
  const tines = [2, 3, 4, 4][t - 1]!;
  const tips = [-1, 1].flatMap((k) => antler(h, k, 1.74, as, tines, horn));

  // Bark bracers over the sleeves.
  for (const [k, arm] of [[-1, f.armLAbs], [1, f.armRAbs]] as const) limb(arm, V(k * 0.274, 1.07, 0.065), V(k * 0.281, 0.965, 0.11), 0.054, 0.056, bark, 6);

  // The gnarled staff: a crooked shaft that splits round a mossy stone.
  const staff = G(f.rhAbs);
  const SX = 0.32, SZ = 0.14;
  const top = t >= 3 ? 2.12 : 2.0;
  const shaft = [V(SX, 0.02, SZ), V(SX + 0.02, 0.7, SZ - 0.01), V(SX - 0.015, 1.3, SZ + 0.015), V(SX + 0.01, top - 0.2, SZ)];
  for (let i = 0; i < 3; i++) limb(staff, shaft[i]!, shaft[i + 1]!, 0.024 - i * 0.002, 0.022 - i * 0.002, bark, 6);
  const st = V(SX, top - 0.05, SZ);
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + 0.4;
    const out = V(SX + Math.cos(a) * 0.075, top - 0.08, SZ + Math.sin(a) * 0.075);
    limb(staff, shaft[3]!, out, 0.016, 0.012, bark, 5);
    limb(staff, out, V(SX + Math.cos(a + 0.6) * 0.04, top + 0.06, SZ + Math.sin(a + 0.6) * 0.04), 0.012, 0.006, bark, 4);
  }
  const gem = M(new THREE.DodecahedronGeometry(0.065, 0), rock, staff);
  gem.position.copy(st);
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2;
    const m = M(new THREE.DodecahedronGeometry(0.03, 0), moss, staff);
    m.position.set(SX + Math.cos(a) * 0.05, top - 0.13 + (i % 2) * 0.03, SZ + Math.sin(a) * 0.05);
    m.scale.y = 0.6;
  }
  limb(staff, V(SX + 0.04, top - 0.12, SZ + 0.03), V(SX + 0.06, top - 0.42, SZ + 0.05), 0.006, 0.004, moss, 3);

  // II: leaf pauldrons, a necklace of river stones, moss on the shoulders and a herb pouch.
  if (t >= 2) {
    for (const k of [-1, 1]) {
      for (let i = 0; i < 5; i++) {
        const a = k * (Math.PI / 2) + ((i - 2) / 2) * 0.7;
        leaf(b, V(k * 0.25 + Math.sin(a) * 0.02, 1.46 - Math.abs(i - 2) * 0.03, Math.cos(a) * 0.08), a, i, 1.1, 0.7);
      }
      const ms = M(new THREE.DodecahedronGeometry(0.05, 0), moss, b);
      ms.position.set(k * 0.22, 1.47, -0.04);
      ms.scale.y = 0.5;
    }
    for (let i = 0; i < 7; i++) {
      const a = ((i - 3) / 3) * 0.95;
      M(new THREE.DodecahedronGeometry(0.019, 0), rock, b).position.set(Math.sin(a) * 0.16, 1.42 - Math.abs(i - 3) * 0.012, Math.cos(a) * 0.185);
    }
    M(new THREE.BoxGeometry(0.08, 0.09, 0.05), bark, b).position.set(-0.18, 0.9, 0.08);
  }
  // III: a mantle of leaves over the shoulders, ivy hanging from the antlers and a wreath round the hood.
  if (t >= 3) {
    for (let i = 0; i < 14; i++) {
      const a = (i / 14) * Math.PI * 2;
      leaf(b, V(Math.sin(a) * 0.235, 1.44, Math.cos(a) * 0.2), a, i, 1.15, 0.5);
    }
    for (const tip of tips.filter((_, i) => i % 3 === 1)) limb(h, tip, tip.clone().add(V(0, -0.2, 0.02)), 0.005, 0.004, moss, 3);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const m = M(new THREE.DodecahedronGeometry(0.025, 0), i % 2 ? moss : leaves[0]!, hg);
      m.position.set(Math.sin(a) * 0.14, 1.75, Math.cos(a) * 0.13 - 0.02);
    }
  }
  // IV: the stone wakes with green light, spirit motes drift between the antlers, a moss-green aura.
  let motes: THREE.Sprite[] = [];
  if (t >= 4) {
    const wake = emis('#e6ffd0', '#7fd05a', 1.3, { flatShading: true });
    gem.material = wake;
    for (const y of [0.5, 0.8, 1.1]) M(new THREE.BoxGeometry(0.034, 0.03, 0.03), wake, staff).position.set(SX + 0.004, y, SZ + 0.012);
    glowSprite(staff, '#9fe07a', 0.42, st, 0.6);
    motes = [0, 1, 2].map((i) => glowSprite(h, '#bff09a', 0.09, V((i - 1) * 0.14, 2.0, 0), 0.85, true));
    aura(c.g, '#6fa05a');
  }
  const tick = (n: number): void => {
    motes.forEach((m, i) => {
      const a = n * 0.25 + i * 2.1;
      m.position.set(Math.cos(a) * 0.17, 1.95 + 0.06 * Math.sin(n * 0.4 + i), Math.sin(a) * 0.12);
    });
  };
  tick(0);
  return hero(c, f, o, [2.26, 2.3, 2.53, 2.59][t - 1]!, tick);
};

/**
 * The druid's bear form: a huge brown bear, mossy along the hump, with lichen stones and short antlers that
 * say it is still the druid. It shares the hero rig so the game poses it with the same code: the front legs
 * are the arms (mirrored: armR is the left front leg, armL the right one), the hind legs the legs (legL left),
 * the head and body pivots as a hero's. `weapon` and `offhand` are empty groups at the armR and armL paws
 * (for hit effects). The whole-figure pivot rolls it onto its left side for the 'down' pose. About 2.5–3.1
 * units tall (antlers included), 1.2 wide and 3 long.
 */
export const bear = (o?: HeroOpts): Model => {
  const c = begin(o, 1.1, 9);
  const t = o?.tier ?? 1;
  const S = 1.18;
  const fur = std('#3d2b1f', { roughness: 1, bumpScale: 0.04 }), furDk = std('#2a1e16', { roughness: 1, bumpScale: 0.04 });
  const muzzle = std('#6a5240', { roughness: 0.95 });
  const moss = std('#5d7a3a', { roughness: 1, bumpScale: 0.04 }), mossDk = std('#3f5a2e', { roughness: 1, bumpScale: 0.04 });
  const horn = std('#a8977a', { roughness: 0.9 });
  const rock = stone('#7d7a72');
  // The figure pivot sits at the bear's left flank, its X axis turned to run nose-to-tail: the 'down' pose's roll.
  const PX = -0.55 * S;
  const outer = G(c.g, PX, 0, 0);
  outer.name = 'bear';
  outer.rotation.y = Math.PI / 2;
  const figP = G(outer);
  figP.name = 'figure';
  const unrot = G(figP);
  unrot.rotation.y = -Math.PI / 2;
  const fig = G(unrot, -PX, 0, 0);
  fig.scale.setScalar(S);

  // Body pivot near the shoulders: a lean forward dips the chest, the rump rises over the hind legs.
  const body = pivot(fig, V(0, 1.0, 0.45), 'body');
  const b = body.abs;
  const trunk = M(new THREE.SphereGeometry(0.6, 14, 10), fur, b);
  trunk.position.set(0, 1.02, 0);
  trunk.scale.set(0.8, 0.72, 1.25);
  const hump = M(new THREE.SphereGeometry(0.45, 12, 8), fur, b);
  hump.position.set(0, 1.3, 0.35);
  hump.scale.set(0.95, 0.85, 0.95);
  const rump = M(new THREE.SphereGeometry(0.46, 12, 8), fur, b);
  rump.position.set(0, 1.0, -0.42);
  rump.scale.set(0.95, 0.95, 0.95);
  M(new THREE.SphereGeometry(0.1, 6, 4), furDk, b).position.set(0, 1.08, -0.86);
  // Shaggy ridge of fur down the spine, and a darker belly.
  for (let i = 0; i < 7; i++) {
    const z = 0.55 - i * 0.18;
    const y = 1.62 - Math.abs(z - 0.3) * 0.38;
    const tuft = M(new THREE.ConeGeometry(0.09, 0.2, 5), i % 2 ? furDk : fur, b);
    tuft.position.set(0, y, z);
    tuft.rotation.x = -0.9;
  }
  const belly = M(new THREE.SphereGeometry(0.42, 10, 6), furDk, b);
  belly.position.set(0, 0.78, 0.05);
  belly.scale.set(0.85, 0.5, 1.3);

  // Front legs: hung from the shoulders on the body (arms), thick, with bone claws. Each has an elbow and a
  // wrist pivot (anim bones): the leg is drawn in two lengths that meet at the elbow, as one taper.
  const paw = (abs: THREE.Group, k: number, top: THREE.Vector3, foot: THREE.Vector3): { elbow: THREE.Group; wrist: THREE.Group } => {
    const end = V(foot.x, foot.y + 0.12, foot.z);
    const mid = top.clone().lerp(end, 0.5);
    limb(abs, top, mid, 0.19, 0.165, fur, 8);
    const elbow = pivot(abs, mid, k < 0 ? 'elbowL' : 'elbowR');
    limb(elbow.abs, mid, end, 0.165, 0.14, fur, 8);
    const wrist = pivot(elbow.abs, V(end.x, end.y + 0.01, end.z - 0.01), k < 0 ? 'wristL' : 'wristR');
    const p = M(new THREE.SphereGeometry(0.15, 8, 5), furDk, wrist.abs);
    p.position.set(foot.x, foot.y + 0.095, foot.z + 0.04);
    p.scale.set(1, 0.6, 1.25);
    for (let i = 0; i < 4; i++) {
      const cl = M(new THREE.ConeGeometry(0.022, 0.09, 4), horn, wrist.abs);
      cl.position.set(foot.x + (i - 1.5) * 0.06 + k * 0.01, foot.y + 0.06, foot.z + 0.2);
      cl.rotation.x = Math.PI / 2 + 0.4;
    }
    return { elbow: elbow.p, wrist: wrist.p };
  };
  const arms: { p: THREE.Group; abs: THREE.Group }[] = [];
  const fore: { elbow: THREE.Group; wrist: THREE.Group }[] = [];
  for (const k of [-1, 1]) {
    const at = V(k * 0.3, 1.12, 0.48);
    const a = pivot(b, at, k < 0 ? 'armL' : 'armR');
    const sh = M(new THREE.SphereGeometry(0.24, 10, 7), fur, a.abs);
    sh.position.set(k * 0.3, 1.05, 0.48);
    fore.push(paw(a.abs, k, V(k * 0.31, 1.0, 0.5), V(k * 0.32, 0, 0.56)));
    arms.push(a);
  }
  // Hind legs on the figure (legs): a big haunch, the hock bent back, a broad hind paw (hock and paw pivots).
  const legs: THREE.Group[] = [];
  const hind: { hock: THREE.Group; foot: THREE.Group }[] = [];
  for (const k of [-1, 1]) {
    const l = pivot(fig, V(k * 0.32, 1.0, -0.45), k < 0 ? 'legL' : 'legR');
    const haunch = M(new THREE.SphereGeometry(0.3, 10, 7), fur, l.abs);
    haunch.position.set(k * 0.32, 0.92, -0.42);
    haunch.scale.set(0.8, 1.05, 1);
    limb(l.abs, V(k * 0.33, 0.8, -0.45), V(k * 0.33, 0.38, -0.6), 0.17, 0.12, fur, 8);
    const hock = pivot(l.abs, V(k * 0.33, 0.38, -0.6), k < 0 ? 'hockL' : 'hockR');
    limb(hock.abs, V(k * 0.33, 0.38, -0.6), V(k * 0.33, 0.1, -0.5), 0.12, 0.11, fur, 8);
    const foot = pivot(hock.abs, V(k * 0.33, 0.11, -0.5), k < 0 ? 'footL' : 'footR');
    const p = M(new THREE.SphereGeometry(0.14, 8, 5), furDk, foot.abs);
    p.position.set(k * 0.33, 0.085, -0.44);
    p.scale.set(1, 0.55, 1.45);
    legs.push(l.p);
    hind.push({ hock: hock.p, foot: foot.p });
  }

  // Head on a thick neck: a heavy brow, a lighter muzzle, small ears and short antlers.
  const head = pivot(b, V(0, 1.38, 0.78), 'head');
  const hd = head.abs;
  limb(b, V(0, 1.3, 0.55), V(0, 1.34, 0.85), 0.3, 0.24, fur, 8);
  const skull = M(new THREE.SphereGeometry(0.27, 12, 9), fur, hd);
  skull.position.set(0, 1.36, 1.02);
  skull.scale.set(1, 0.9, 1.05);
  const snout = M(new THREE.CylinderGeometry(0.11, 0.15, 0.26, 9), muzzle, hd);
  snout.rotation.x = Math.PI / 2 + 0.12;
  snout.position.set(0, 1.27, 1.29);
  M(new THREE.SphereGeometry(0.06, 7, 5), BLACK, hd).position.set(0, 1.29, 1.42);
  const jawP = pivot(hd, V(0, 1.2, 1.13), 'jaw');
  const jaw = M(new THREE.BoxGeometry(0.17, 0.06, 0.22), muzzle, jawP.abs);
  jaw.position.set(0, 1.17, 1.24);
  for (const k of [-1, 1]) {
    const ear = M(new THREE.SphereGeometry(0.07, 7, 5), furDk, hd);
    ear.position.set(k * 0.19, 1.58, 0.95);
    ear.scale.set(1, 1, 0.6);
    M(new THREE.SphereGeometry(0.03, 6, 4), BLACK, hd).position.set(k * 0.11, 1.42, 1.24);
  }
  const as = [1.4, 1.58, 1.75, 1.9][t - 1]!;
  const tines = [2, 3, 4, 4][t - 1]!;
  const ant = G(hd, 0, 0, 0.98);
  const tips = [-1, 1].flatMap((k) => antler(ant, k, 1.56, as, tines, horn));

  // Moss along the hump and back, with lichen stones in it.
  const mossAt = (n: number, seed: number): void => {
    for (let i = 0; i < n; i++) {
      const u = ((i * 0.618 + seed) % 1) - 0.5;
      const z = 0.55 - (i / Math.max(1, n - 1)) * 0.95;
      const x = u * 0.45;
      const y = 1.6 - Math.abs(z - 0.3) * 0.4 - Math.abs(x) * 0.35;
      const m = M(new THREE.DodecahedronGeometry(0.1 + (i % 3) * 0.025, 0), i % 2 ? moss : mossDk, b);
      m.position.set(x, y - 0.04, z);
      m.scale.set(1.2, 0.45, 1.2);
    }
  };
  mossAt(5, 0.1);
  M(new THREE.DodecahedronGeometry(0.07, 0), rock, b).position.set(0.17, 1.55, 0.3);

  // II: more moss, a mantle of leaves at the scruff and moss on the haunches.
  if (t >= 2) {
    mossAt(6, 0.47);
    for (let i = 0; i < 10; i++) {
      const a = ((i - 4.5) / 4.5) * 1.5;
      const lg = G(b, Math.sin(a) * 0.36, 1.45 - Math.abs(a) * 0.1, 0.62 + Math.cos(a) * 0.12);
      lg.rotation.y = a;
      const l = M(new THREE.ConeGeometry(0.06, 0.18, 4), i % 2 ? moss : mossDk, lg);
      l.rotation.x = Math.PI - 0.6;
      l.scale.z = 0.32;
    }
    for (const leg of legs) {
      const k = leg === legs[0] ? -1 : 1;
      const m = M(new THREE.DodecahedronGeometry(0.11, 0), moss, legAbs(leg));
      m.position.set(k * 0.4, 1.05, -0.4);
      m.scale.set(0.6, 0.5, 1);
    }
  }
  // III: standing stones grown into the hump, ivy trailing from the antlers.
  if (t >= 3) {
    for (const [x, z, s] of [[-0.14, 0.42, 1], [0.05, 0.12, 0.8], [-0.1, -0.15, 0.7]] as const) {
      const r = M(new THREE.DodecahedronGeometry(0.06, 0), rock, b);
      r.position.set(x, 1.6 - Math.abs(z - 0.3) * 0.4, z);
      r.scale.set(s, 1.8 * s, s);
    }
    for (const tip of tips.filter((_, i) => i % 3 === 1)) limb(ant, tip, tip.clone().add(V(0, -0.3, 0.03)), 0.009, 0.006, moss, 3);
  }
  // IV: the eyes and the stones burn green; spirit light at the antlers and a moss-green aura.
  if (t >= 4) {
    const wake = emis('#e6ffd0', '#7fd05a', 1.3, { flatShading: true });
    for (const k of [-1, 1]) {
      M(new THREE.OctahedronGeometry(0.032, 0), wake, hd).position.set(k * 0.11, 1.42, 1.25);
      glowSprite(hd, '#9fe07a', 0.14, V(k * 0.11, 1.42, 1.28), 0.85);
    }
    b.traverse((m) => {
      if ((m as THREE.Mesh).isMesh && (m as THREE.Mesh).material === rock) (m as THREE.Mesh).material = wake;
    });
    glowSprite(b, '#9fe07a', 0.5, V(0, 1.7, 0.2), 0.4);
    glowSprite(hd, '#bff09a', 0.6, V(0, 1.56 + 0.3 * as, 0.98), 0.3);
    glowSprite(c.g, '#6fa05a', 3.2, V(0, 1.1 * S, 0), 0.12);
    glowSprite(c.g, '#6fa05a', 2.2, V(0, 0.12, 0), 0.15);
  }

  // Mirrored on purpose: the pose that splays a hero's arms outward when it falls ('down') then tucks the
  // paws in against the belly of the bear lying on its side, instead of driving the lower one into the table.
  const [armR, armL] = arms as [{ p: THREE.Group; abs: THREE.Group }, { p: THREE.Group; abs: THREE.Group }];
  armR.p.name = 'armR';
  armL.p.name = 'armL';
  const weapon = G(armR.abs, -0.32, 0.06, 0.62);
  weapon.name = 'weapon';
  const offhand = G(armL.abs, 0.32, 0.06, 0.62);
  offhand.name = 'offhand';
  const rig: HeroRig = { body: body.p, armL: armL.p, armR: armR.p, legL: legs[0]!, legR: legs[1]!, head: head.p, weapon, offhand, figure: figP };
  const m = hero(c, { fig: outer, rig }, o, [2.55, 2.6, 2.85, 2.95][t - 1]!);
  // Its own skeleton for the GPU clips (anim/creatures/allies.ts), named by side: `L` legs are on -X.
  const bones: Record<string, THREE.Object3D> = {
    figure: figP, body: body.p, head: head.p, jaw: jawP.p,
    frontL: arms[0]!.p, elbowL: fore[0]!.elbow, wristL: fore[0]!.wrist, frontR: arms[1]!.p, elbowR: fore[1]!.elbow, wristR: fore[1]!.wrist,
    hindL: legs[0]!, hockL: hind[0]!.hock, footL: hind[0]!.foot, hindR: legs[1]!, hockR: hind[1]!.hock, footR: hind[1]!.foot,
  };
  m.skel = { bones: [c.g, ...Object.values(bones)], names: ['root', ...Object.keys(bones)] };
  return m;
};

/* ---------- worker golems ---------- */

export interface GolemOpts extends BaseOpts {
  /** 0 mason, 1 woodcutter, 2 miner, 3 lamplighter. */
  variant: 0 | 1 | 2 | 3;
}

export const golem = (o: GolemOpts): Model => {
  const c = begin(o, 0.4, 10 + o.variant);
  const st = stone('#7a7266'), rune = emis('#8fe9ff', '#2fbfe0', 0.9);
  // Bones (see anim/creatures/allies.ts): the trunk pivots at the hips, the head on the neck, each leg at its
  // hip, each arm at its shoulder and the tool at the right fist. Everything carried rides the trunk (or the head).
  const body = pivot(c.g, V(0, 0.18, 0), 'body');
  const p = body.abs;
  const head = pivot(p, V(0, 0.47, 0.02), 'head');
  const hd = head.abs;
  M(new THREE.BoxGeometry(0.26, 0.28, 0.2), st, p).position.y = 0.32;
  M(new THREE.BoxGeometry(0.14, 0.12, 0.14), st, hd).position.set(0, 0.53, 0.02);
  const arms: THREE.Group[] = [];
  const legs: THREE.Group[] = [];
  for (const k of [-1, 1]) {
    M(new THREE.BoxGeometry(0.03, 0.02, 0.01), rune, hd).position.set(k * 0.035, 0.54, 0.095);
    const a = G(p, k * 0.16, 0.42, 0);
    a.name = k < 0 ? 'armL' : 'armR';
    limb(a, V(0, 0, 0), V(k * 0.02, -0.22, 0.05), 0.045, 0.05, st, 6);
    arms.push(a);
    const l = pivot(c.g, V(k * 0.07, 0.18, 0), k < 0 ? 'legL' : 'legR');
    limb(l.abs, V(k * 0.07, 0.18, 0), V(k * 0.07, 0, 0), 0.05, 0.055, st, 6);
    legs.push(l.p);
  }
  M(new THREE.BoxGeometry(0.08, 0.02, 0.01), rune, p).position.set(0, 0.34, 0.105);
  const [armL, armR] = arms as [THREE.Group, THREE.Group];
  const hand = G(armR, 0.02, -0.22, 0.06);
  hand.name = 'tool';
  let height: number;
  let lantern: THREE.Group | null = null;
  const flames: { flick(): void }[] = [];
  switch (o.variant) {
    case 0: { // mason: dressed block on the head, chisel in hand
      M(new THREE.BoxGeometry(0.2, 0.14, 0.16), stone('#b5ab98'), hd).position.set(0, 0.68, 0.02);
      limb(hand, V(0, -0.02, 0), V(0, 0.08, 0.06), 0.012, 0.012, DSTEEL, 5);
      M(new THREE.BoxGeometry(0.05, 0.06, 0.05), WOOD, hand).position.set(0, -0.04, -0.01);
      height = 0.76;
      break;
    }
    case 1: { // woodcutter: log on the shoulder, axe in hand
      const log = M(new THREE.CylinderGeometry(0.05, 0.055, 0.5, 7), WOOD, p);
      log.rotation.x = Math.PI / 2;
      log.position.set(-0.14, 0.6, -0.02);
      for (const z of [-0.25, 0.23]) {
        const end = M(new THREE.CircleGeometry(0.05, 7), std('#a07850', { roughness: 0.9 }), p);
        end.position.set(-0.14, 0.6, z);
        end.rotation.y = z < 0 ? Math.PI : 0;
      }
      limb(hand, V(0, -0.06, 0), V(0, 0.22, 0.04), 0.012, 0.012, DWOOD, 5);
      const bl = M(new THREE.CylinderGeometry(0.06, 0.06, 0.012, 8, 1, false, 0, Math.PI), DSTEEL, hand);
      bl.rotation.z = Math.PI / 2;
      bl.position.set(0.01, 0.19, 0.04);
      height = 0.7;
      break;
    }
    case 2: { // miner: pickaxe, basket of glowing ore on the back
      limb(hand, V(0, -0.05, 0), V(0, 0.24, 0.03), 0.012, 0.012, DWOOD, 5);
      const pick = M(new THREE.CylinderGeometry(0.01, 0.018, 0.26, 5), DSTEEL, hand);
      pick.rotation.x = Math.PI / 2;
      pick.position.set(0, 0.23, 0.03);
      M(new THREE.CylinderGeometry(0.09, 0.07, 0.14, 8, 1, true), std('#6b5236', { side: THREE.DoubleSide, roughness: 1 }), p).position.set(0, 0.42, -0.16);
      const ore = emis('#bfe8ff', '#3aa6ff', 0.9, { flatShading: true });
      for (let i = 0; i < 4; i++) {
        const s = M(new THREE.OctahedronGeometry(0.03 + c.rng() * 0.02), ore, p);
        s.position.set(c.R() * 0.1, 0.5, -0.16 + c.R() * 0.08);
        s.scale.y = 1.6;
      }
      glowSprite(p, '#5ab8ff', 0.3, V(0, 0.52, -0.16), 0.5);
      height = 0.62;
      break;
    }
    default: { // lamplighter: pole with a hanging lantern (its own bone, so it swings)
      limb(hand, V(0, -0.15, 0), V(0, 0.75, 0), 0.012, 0.012, DWOOD, 5);
      limb(hand, V(0, 0.75, 0), V(0, 0.75, 0.16), 0.01, 0.01, DWOOD, 4);
      lantern = G(hand, 0, 0.75, 0.16);
      lantern.name = 'lantern';
      const lan = G(lantern, 0, -0.12, 0);
      M(new THREE.CylinderGeometry(0.04, 0.045, 0.08, 6, 1, true), DSTEEL, lan);
      M(new THREE.ConeGeometry(0.05, 0.04, 6), DSTEEL, lan).position.y = 0.06;
      flames.push(flame(lan, V(0, -0.03, 0), 0.4, c.rng));
      height = 0.98;
    }
  }
  // Only the flame boils here: the stride, the swing of the tool and the bob are clips.
  const t = (): void => {
    for (const f of flames) f.flick();
  };
  t();
  const bones: Record<string, THREE.Object3D> = { body: body.p, head: head.p, legL: legs[0]!, legR: legs[1]!, armL, armR, tool: hand };
  if (lantern) bones.lantern = lantern;
  return done(c, height, t, undefined, { bones: [c.g, ...Object.values(bones)], names: ['root', ...Object.keys(bones)] });
};
